// @ts-nocheck
(function initializeNoDrillConfigurator() {
  'use strict';

  const STORAGE_KEY = globalThis.NoDrillCatalogData?.storageNamespace || 'ndr-preview-order-v4';
  const ORDER_ACCESSORIES_STORAGE_KEY = `${STORAGE_KEY}:order-accessories`;
  const STANDARD_BRACKET_NOTICE_STORAGE_KEY = `${STORAGE_KEY}:standard-bracket-notice-seen`;

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(cents || 0) / 100);
  }

  // Black Friday sale (NDR-D-145, Albert, Nov 2 2026): every shade is 51% off, so the band by the builder price
  // shows on every roller builder while the sale runs. no-drill-builder-steps.js owns the sale dates and prices.
  function saleBandShown(now = Date.now()) {
    return Boolean(globalThis.NoDrillBuilderSteps?.saleActive?.(now));
  }

  function priceIsPending(rules) {
    // NDR-D-038: all customer prices stay pending, including legacy drafts.
    // While this returns true, the save / edit / review-order paths below that check pricePending never run,
    // and assets/no-drill-cart-bridge.js owns the cart buttons (it rewrites them after each render).
    // Change cart buttons there, not here.
    return true;
  }

  function createConfigurationId() {
    return `ndr-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  function loadOrderAccessoryQuantities() {
    try {
      const value = JSON.parse(globalThis.sessionStorage?.getItem(ORDER_ACCESSORIES_STORAGE_KEY) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch (_error) {
      return {};
    }
  }

  function saveOrderAccessoryQuantities(quantities) {
    globalThis.sessionStorage?.setItem(ORDER_ACCESSORIES_STORAGE_KEY, JSON.stringify(quantities));
  }

  function isSavedConfigurationRecord(value) {
    return Boolean(
      value
      && typeof value === 'object'
      && !Array.isArray(value)
      && typeof value.configurationId === 'string'
      && value.configurationId.trim()
      && typeof value.categoryId === 'string'
      && value.categoryId.trim()
      && (value.options === undefined || (value.options && typeof value.options === 'object' && !Array.isArray(value.options)))
      && (value.accessories === undefined || Array.isArray(value.accessories))
    );
  }

  // No shade has a pull chain or cord (NDR-D-063): every shade is Cordless or Motorized.
  // The pull_chain / chain_material code left in this file only turns old saved designs and old links
  // into Cordless and keeps chain inputs off. Do not add chain options back.
  function normalizeRestoredControl(configuration) {
    if (!['pull_chain', 'cord'].includes(configuration.control) && !configuration.options?.chain_material) return configuration;
    const normalized = {
      ...configuration,
      control: 'cordless',
      controlLabel: 'Cordless',
      options: { ...configuration.options, chain_material: '' },
    };
    delete normalized.controlSide;
    return normalized;
  }

  function sanitizeSavedConfigurationRecord(record, core) {
    record = normalizeRestoredControl(record);
    const options = { ...(record.options || {}) };
    delete options.installation_surface;
    return {
      ...record,
      options,
      optionSummary: core.sanitizeOptionSummary(record.optionSummary),
    };
  }

  function loadOrder(core) {
    try {
      const value = JSON.parse(globalThis.sessionStorage?.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value)
        ? value.filter(isSavedConfigurationRecord).map((record) => sanitizeSavedConfigurationRecord(record, core))
        : [];
    } catch (_error) {
      return [];
    }
  }

  function saveOrder(order) {
    globalThis.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify(order));
  }

  function readSelectorMedia(section) {
    const node = section.querySelector('[data-selector-media]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('No Drill selector media could not be loaded.', error);
      return {};
    }
  }

  function setRadioLabelPrice(label, cents, rules) {
    const price = document.createElement('span');
    price.className = 'ndr-choice__price';
    price.textContent = priceIsPending(rules)
      ? 'Price pending'
      : cents
        ? `+${formatMoney(cents, rules.currency)}`
        : 'Included';
    label.append(price);
  }

  function syncEstimatedOptionPriceLabels(section, configuration, pricingApi) {
    if (!pricingApi?.profile) return;
    const applies = configuration?.options?.fabric_family === pricingApi.profile.familyId;
    const estimateControl = (control) => applies ? pricingApi.priceConfiguration?.({
      ...configuration,
      control,
      options: { ...configuration.options, chain_material: '' },
    }) : null;
    const base = estimateControl('cordless');
    section.querySelectorAll('[name="control"]').forEach((input) => {
      const badge = input.closest('label')?.querySelector('.ndr-choice__price');
      if (!badge) return;
      if (input.disabled || !['cordless', 'motorized'].includes(input.value)) {
        badge.textContent = '';
        badge.hidden = true;
        return;
      }
      const estimate = input.value === 'cordless' ? base : estimateControl(input.value);
      const delta = estimate?.valid && base?.valid ? Math.max(0, estimate.totalCents - base.totalCents) : null;
      badge.hidden = delta === 0;
      badge.textContent = delta === null ? 'Price pending' : delta === 0 ? '' : `+${formatMoney(delta, estimate.currency)}`;
    });
  }

  function makeChoice({ name, value, labelText, note, disabled, checked, type = 'radio', swatchHex, swatchImage, weaveDetailImage, selectorMedia, selectorMediaByHeadrail, recommended, topPick, topPickLabel, surchargeCents }, rules) {
    const label = document.createElement('label');
    const hasSwatch = Boolean(swatchHex || swatchImage);
    const hasSelectorMedia = Boolean(selectorMedia);
    label.className = `ndr-choice${disabled ? ' is-disabled' : ''}${hasSwatch ? ' ndr-choice--swatch' : ''}${hasSelectorMedia ? ' ndr-choice--media' : ''}`;

    const input = document.createElement('input');
    input.type = type;
    input.name = name;
    input.value = value;
    input.disabled = Boolean(disabled);
    input.checked = Boolean(checked && !disabled);
    if (selectorMediaByHeadrail && Object.keys(selectorMediaByHeadrail).length > 0) {
      input.dataset.selectorMediaByHeadrail = JSON.stringify(selectorMediaByHeadrail);
    }

    const copy = document.createElement('span');
    if (selectorMedia) {
      const media = document.createElement('span');
      media.className = 'ndr-choice__media';
      media.setAttribute('aria-hidden', 'true');
      const thumbnailImage = document.createElement('img');
      thumbnailImage.className = 'ndr-choice__media-image';
      thumbnailImage.src = selectorMedia;
      thumbnailImage.alt = '';
      thumbnailImage.width = 160;
      thumbnailImage.height = 120;
      thumbnailImage.decoding = 'async';
      thumbnailImage.addEventListener('load', () => {
        thumbnailImage.hidden = false;
        media.classList.remove('is-unavailable');
      });
      thumbnailImage.addEventListener('error', () => {
        thumbnailImage.hidden = true;
        media.classList.add('is-unavailable');
      });
      media.append(thumbnailImage);
      copy.append(media);
    } else if (swatchImage) {
      const image = document.createElement('img');
      image.className = 'ndr-choice__swatch-image';
      image.src = swatchImage;
      image.alt = '';
      image.width = 52;
      image.height = 52;
      image.decoding = 'async';
      image.addEventListener('error', () => {
        image.hidden = true;
      }, { once: true });
      copy.append(image);
    } else if (swatchHex) {
      const swatch = document.createElement('i');
      swatch.className = 'ndr-choice__swatch';
      swatch.style.backgroundColor = swatchHex;
      copy.append(swatch);
    }
    const strong = document.createElement('strong');
    strong.textContent = labelText;
    if (recommended || topPick) {
      const heading = document.createElement('span');
      heading.className = 'ndr-choice__heading';
      heading.append(strong);
      if (topPick) {
        // NDR-D-091: small Top Pick pill beside the color name; the strong keeps the exact name.
        const pick = document.createElement('span');
        pick.className = 'ndr-choice__top-pick';
        pick.textContent = topPickLabel || 'Top Pick';
        heading.append(pick);
      }
      if (recommended) {
        const recommendation = document.createElement('span');
        recommendation.className = 'ndr-choice__recommendation';
        recommendation.textContent = rules.recommendationLabels?.[name.replace('option_', '')] || rules.recommendationLabel || 'Recommended';
        heading.append(recommendation);
      }
      copy.append(heading);
    } else {
      copy.append(strong);
    }
    if (note) {
      const small = document.createElement('small');
      small.textContent = note;
      copy.append(small);
    }
    label.append(input, copy);
    if (surchargeCents !== undefined) setRadioLabelPrice(label, surchargeCents, rules);
    if (!weaveDetailImage) return label;

    const shell = document.createElement('div');
    const trigger = document.createElement('button');
    shell.className = 'ndr-weave-choice';
    shell.dataset.weaveChoice = '';
    shell.dataset.weaveDetail = weaveDetailImage;
    shell.dataset.weaveLabel = labelText;
    trigger.type = 'button';
    trigger.className = 'ndr-weave-detail-trigger';
    trigger.dataset.weaveDetailTrigger = '';
    trigger.textContent = 'View weave';
    trigger.setAttribute('aria-label', `See a close-up of the weave for ${labelText}`);
    trigger.setAttribute('aria-expanded', 'false');
    shell.append(label, trigger);
    return shell;
  }

  function renderOptionGroups(section, rules, selectorMedia = {}) {
    const roots = new Map(
      Array.from(section.querySelectorAll('[data-product-option-stage]'))
        .map((root) => [root.dataset.productOptionStage, root]),
    );
    roots.forEach((root) => root.replaceChildren());
    // NDR-D-091: Top Pick colors come from the V2 catalog data. Pages without it (V1) show no badges.
    const topPicks = globalThis.NoDrillCatalogData?.topPicks;
    const topPickColors = new Set(Array.isArray(topPicks?.colors) ? topPicks.colors : []);
    const topPickLabel = typeof topPicks?.label === 'string' && topPicks.label ? topPicks.label : 'Top Pick';
    rules.optionGroups.forEach((group) => {
      const stage = group.stage || 'fabric';
      const root = roots.get(stage) || roots.get('fabric');
      if (!root) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'ndr-option-group';
      wrapper.dataset.optionGroup = group.id;
      const heading = document.createElement('h3');
      heading.textContent = group.label;
      const guidance = document.createElement('p');
      guidance.className = 'ndr-option-group__note';
      // NDR-D-080: the Liquid section owns this reversible display gate.
      const hideSampleNote = section.dataset.hideFreeSamples === 'true'
        && group.note === 'Use a fabric sample to see your color in person.';
      guidance.textContent = hideSampleNote ? '' : (group.note || '');
      guidance.hidden = !guidance.textContent;
      const choices = document.createElement('div');
      choices.className = group.display === 'swatch'
        ? 'ndr-swatch-grid'
        : `ndr-choice-grid${group.id === 'fabric_color' ? ' ndr-choice-grid--color' : ''}`;
      group.options.forEach((option) => {
        const inputName = `option_${group.id}`;
        const selectorMediaByHeadrail = Object.fromEntries(
          Object.entries(option.selectorMediaKeyByHeadrail || {})
            .map(([headrail, mediaKey]) => [headrail, selectorMedia[mediaKey]])
            .filter(([, mediaSource]) => Boolean(mediaSource)),
        );
        choices.append(
          makeChoice(
            {
              name: inputName,
              value: option.id,
              labelText: option.label,
              note: option.note,
              swatchHex: option.swatchHex,
              swatchImage: option.swatchImage,
              weaveDetailImage: option.weaveDetailImage,
              selectorMedia: group.id === 'fabric_color' ? undefined : selectorMedia[option.selectorMediaKey],
              selectorMediaByHeadrail,
              recommended: rules.recommendedOptions?.[group.id] === option.id,
              topPick: group.id === 'fabric_color' && topPickColors.has(option.id),
              topPickLabel,
              checked: option.id === group.defaultOption,
            },
            rules,
          ),
        );
      });
      const waiting = document.createElement('p');
      waiting.className = 'ndr-option-group__waiting';
      waiting.dataset.optionWaiting = '';
      waiting.textContent = group.waitingLabel || 'Make the choice above first.';
      waiting.hidden = true;
      wrapper.append(heading, guidance, choices, waiting);
      root.append(wrapper);
    });
    section.querySelectorAll('[data-option-stage-fieldset]').forEach((fieldset) => {
      fieldset.hidden = !fieldset.querySelector('.ndr-option-group');
    });
  }

  function setupWeaveDetailPreviews(section) {
    const choices = Array.from(section.querySelectorAll('[data-weave-choice]'));
    if (choices.length === 0) return;

    const popup = document.createElement('div');
    const image = document.createElement('img');
    const copy = document.createElement('div');
    const eyebrow = document.createElement('small');
    const label = document.createElement('strong');
    const hint = document.createElement('span');
    popup.className = 'ndr-weave-preview';
    popup.hidden = true;
    popup.setAttribute('role', 'tooltip');
    popup.dataset.weavePreview = '';
    image.width = 480;
    image.height = 320;
    image.decoding = 'async';
    eyebrow.textContent = 'Close-up of the weave';
    hint.className = 'ndr-weave-preview__hint';
    hint.textContent = 'Tap anywhere to close';
    copy.append(eyebrow, label, hint);
    popup.append(image, copy);
    document.body.append(popup);

    const finePointer = globalThis.matchMedia?.('(hover: hover) and (pointer: fine)');
    let activeChoice = null;
    let clickPinned = false;

    function positionPopup(choice) {
      if (!finePointer?.matches) {
        popup.style.removeProperty('left');
        popup.style.removeProperty('top');
        return;
      }
      const anchor = choice.getBoundingClientRect();
      const box = popup.getBoundingClientRect();
      const gutter = 12;
      const left = Math.min(
        Math.max(anchor.left + (anchor.width - box.width) / 2, gutter),
        globalThis.innerWidth - box.width - gutter,
      );
      const above = anchor.top - box.height - 10;
      const top = above >= gutter
        ? above
        : Math.min(anchor.bottom + 10, globalThis.innerHeight - box.height - gutter);
      popup.style.left = `${Math.round(left)}px`;
      popup.style.top = `${Math.round(Math.max(gutter, top))}px`;
    }

    function closePreview() {
      if (!activeChoice) return;
      activeChoice.querySelector('[data-weave-detail-trigger]')?.setAttribute('aria-expanded', 'false');
      activeChoice = null;
      clickPinned = false;
      popup.hidden = true;
      popup.removeAttribute('data-touch-open');
      image.removeAttribute('src');
      image.alt = '';
    }

    function openPreview(choice, pin = false) {
      if (!choice || choice.hidden || !choice.dataset.weaveDetail) return;
      if (activeChoice && activeChoice !== choice) {
        activeChoice.querySelector('[data-weave-detail-trigger]')?.setAttribute('aria-expanded', 'false');
      }
      activeChoice = choice;
      clickPinned = pin;
      image.src = choice.dataset.weaveDetail;
      image.alt = `Close-up of the weave for ${choice.dataset.weaveLabel}`;
      label.textContent = choice.dataset.weaveLabel;
      choice.querySelector('[data-weave-detail-trigger]')?.setAttribute('aria-expanded', 'true');
      popup.toggleAttribute('data-touch-open', pin);
      popup.hidden = false;
      const position = () => positionPopup(choice);
      if (globalThis.requestAnimationFrame) globalThis.requestAnimationFrame(position);
      else position();
    }

    image.addEventListener('error', () => {
      if (activeChoice) activeChoice.querySelector('[data-weave-detail-trigger]')?.setAttribute('hidden', '');
      closePreview();
    });
    popup.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      closePreview();
    });
    section.addEventListener('pointerover', (event) => {
      if (!finePointer?.matches) return;
      const choice = event.target.closest('[data-weave-choice]');
      if (choice && !choice.contains(event.relatedTarget)) openPreview(choice);
    });
    section.addEventListener('pointerout', (event) => {
      const choice = event.target.closest('[data-weave-choice]');
      if (!clickPinned && choice && choice === activeChoice && !choice.contains(event.relatedTarget)) closePreview();
    });
    section.addEventListener('focusin', (event) => {
      const choice = event.target.closest('[data-weave-choice]');
      if (choice && finePointer?.matches) openPreview(choice);
    });
    section.addEventListener('focusout', (event) => {
      const choice = event.target.closest('[data-weave-choice]');
      if (!clickPinned && choice && choice === activeChoice && !choice.contains(event.relatedTarget)) closePreview();
    });
    section.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-weave-detail-trigger]');
      if (!trigger) return;
      event.preventDefault();
      event.stopPropagation();
      const choice = trigger.closest('[data-weave-choice]');
      if (activeChoice === choice && clickPinned) closePreview();
      else openPreview(choice, true);
    });
    document.addEventListener('click', (event) => {
      if (clickPinned && activeChoice && !activeChoice.contains(event.target)) closePreview();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeChoice) closePreview();
    });
    globalThis.addEventListener('resize', closePreview);
    globalThis.addEventListener('scroll', closePreview, { passive: true });
  }

  function renderMounts(section, rules) {
    const root = section.querySelector('[data-mount-confirmation]');
    root.replaceChildren();
    if (Array.isArray(rules.customizerFlow)) return;
    const mount = rules.mounts?.[rules.defaultMount];
    if (!mount || !mount.enabled || !mount.allowedMountTypes?.includes('inside')) return;
    const fieldset = root.closest('fieldset');
    if (fieldset) fieldset.hidden = false;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'mountSystem';
    input.value = rules.defaultMount;

    const confirmation = document.createElement('div');
    confirmation.className = 'ndr-mount-confirmation';
    const illustration = document.createElement('span');
    illustration.className = 'ndr-mount-confirmation__illustration';
    illustration.setAttribute('aria-hidden', 'true');
    const copy = document.createElement('div');
    const eyebrow = document.createElement('small');
    eyebrow.textContent = rules.mountValidation.status === 'documented-drilled-only'
      ? 'Traditional Mount · screws required'
      : 'Inside opening · included';
    const heading = document.createElement('strong');
    heading.textContent = mount.label;
    const note = document.createElement('p');
    note.textContent = mount.note || 'The headrail fits between the two sides of the opening.';
    copy.append(eyebrow, heading, note);
    confirmation.append(illustration, copy);
    root.append(input, confirmation);
  }

  function renumberVisibleSteps(section) {
    const visibleSteps = Array.from(section.querySelectorAll('.ndr-step')).filter((step) => !step.hidden);
    visibleSteps.forEach((step, index) => {
      const badge = step.querySelector(':scope > legend > span');
      if (badge) badge.textContent = String(index + 1);
      step.dataset.visibleStep = String(index + 1);
    });
    const copy = section.querySelector('[data-guided-steps-copy]');
    if (copy) copy.textContent = `${visibleSteps.length} guided steps + Review`;
    const progress = section.querySelector('[data-configurator-progress]');
    if (progress) {
      progress.replaceChildren();
      visibleSteps.forEach((step, index) => {
        const button = document.createElement('button');
        const number = document.createElement('span');
        const copyNode = document.createElement('span');
        const label = document.createElement('strong');
        const status = document.createElement('small');
        const stage = step.dataset.flowStage || '';
        button.type = 'button';
        button.className = 'ndr-progress__item';
        button.dataset.progressStage = stage;
        number.className = 'ndr-progress__number';
        number.textContent = String(index + 1);
        copyNode.className = 'ndr-progress__copy';
        label.textContent = step.querySelector(':scope > legend strong')?.textContent || `Step ${index + 1}`;
        status.dataset.progressStatus = '';
        status.textContent = 'Choose an option';
        copyNode.append(label, status);
        button.append(number, copyNode);
        progress.append(button);
      });
      const review = document.createElement('button');
      const reviewNumber = document.createElement('span');
      const reviewCopy = document.createElement('span');
      const reviewLabel = document.createElement('strong');
      const reviewStatus = document.createElement('small');
      review.type = 'button';
      review.className = 'ndr-progress__item';
      review.dataset.progressStage = 'review';
      reviewNumber.className = 'ndr-progress__number';
      reviewNumber.textContent = String(visibleSteps.length + 1);
      reviewCopy.className = 'ndr-progress__copy';
      reviewLabel.textContent = 'Review';
      reviewStatus.dataset.progressStatus = '';
      reviewStatus.textContent = 'Choose an option';
      reviewCopy.append(reviewLabel, reviewStatus);
      review.append(reviewNumber, reviewCopy);
      progress.append(review);
      if (!progress.dataset.navigationBound) {
        progress.dataset.navigationBound = 'true';
        progress.addEventListener('click', (event) => {
          const button = event.target.closest('[data-progress-stage]');
          if (!button) return;
          const targetStage = button.dataset.progressStage;
          if (targetStage === 'review') {
            const summary = section.querySelector('[data-flow-stage="review"]');
            summary?.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
            summary?.focus({ preventScroll: true });
            return;
          }
          focusStage(section, targetStage);
        });
      }
    }
  }

  // The page markup is already in this order (same as customizerFlow); this only guards older templates.
  function applyCustomizerFlowOrder(section, rules) {
    if (rules.steppedBuilder) return; // Stepped markup is already in customer order.
    if (rules.mode !== 'catalog-v2' || !Array.isArray(rules.customizerFlow)) return;
    const form = section.querySelector('[data-configurator-form]');
    const errorSummary = form?.querySelector('[data-error-summary]');
    if (!form || !errorSummary) return;
    rules.customizerFlow.forEach((stage) => {
      if (stage === 'review') return;
      const step = form.querySelector(`.ndr-step[data-flow-stage="${stage}"]`);
      if (step) form.insertBefore(step, errorSummary);
    });
  }

  function applyFlowStagePresentation(section, rules) {
    Object.entries(rules.flowStageAliases || {}).forEach(([currentStage, nextStage]) => {
      const step = section.querySelector(`.ndr-step[data-flow-stage="${currentStage}"]`);
      if (step) step.dataset.flowStage = nextStage;
    });
    Object.entries(rules.flowStagePresentation || {}).forEach(([stage, presentation]) => {
      const step = section.querySelector(`.ndr-step[data-flow-stage="${stage}"]`);
      const heading = step?.querySelector(':scope > legend strong');
      const lead = step?.querySelector(':scope > legend small');
      if (heading && presentation.heading) heading.textContent = presentation.heading;
      if (lead && presentation.lead) lead.textContent = presentation.lead;
    });
  }

  function applyCatalogV2ReviewLayout(section, rules) {
    if (rules.mode !== 'catalog-v2') return;
    const layout = section.querySelector('.ndr-configurator__layout');
    const summary = layout?.querySelector(':scope > .ndr-summary');
    const details = summary?.querySelector(':scope > .ndr-summary__details')
      || layout?.querySelector(':scope > .ndr-summary__details')
      || layout?.querySelector('.ndr-review-completion > .ndr-summary__details');
    const configurationCheck = section.querySelector('.ndr-configuration-check');
    const previewPrice = summary?.querySelector('.ndr-summary__preview-price');
    if (!layout || !summary || !details || !configurationCheck) return;
    let completion = layout.querySelector(':scope > .ndr-review-completion');
    if (!completion) {
      completion = document.createElement('div');
      completion.className = 'ndr-review-completion';
      summary.after(completion);
    }
    completion.append(details, configurationCheck);
    if (previewPrice) previewPrice.hidden = false;
    summary.removeAttribute('data-flow-stage');
    summary.removeAttribute('tabindex');
    summary.removeAttribute('aria-labelledby');
    summary.setAttribute('aria-label', 'Live product preview');
    details.dataset.flowStage = 'review';
    details.tabIndex = -1;
    details.setAttribute('role', 'region');
    const heading = details.querySelector('[id]');
    if (heading) details.setAttribute('aria-labelledby', heading.id);
  }

  function setupCatalogV2PreviewRail(section, rules) {
    if (rules.mode !== 'catalog-v2') return;
    const summary = section.querySelector('.ndr-summary');
    const preview = summary?.querySelector('.ndr-summary__preview');
    if (!summary || !preview) return;
    const update = () => {
      const halfHeight = Math.max(0, preview.getBoundingClientRect().height / 2);
      summary.style.setProperty('--ndr-preview-half-height', `${halfHeight}px`);
    };
    update();
    globalThis.requestAnimationFrame?.(update);
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(update);
    observer.observe(preview);
  }

  function renderControls(section, rules, selectorMedia) {
    const root = section.querySelector('[data-control-options]');
    root.replaceChildren();
    rules.controlOrder.forEach((id) => {
      const control = rules.controls[id];
      if (!control || ['pull_chain', 'cord'].includes(id)) return;
      const choice = makeChoice(
        {
          name: 'control',
          value: id,
          labelText: control.label,
          note: rules.id === 'roller-shades'
            ? (control.cardNote && !/chain|cord loop/i.test(control.cardNote) ? control.cardNote : { cordless: 'Raise and lower the shade by hand.', motorized: 'Use a remote to move the shade.' }[id] || control.note)
            : control.cardNote || control.note,
          disabled: !control.enabled,
          checked: false,
          selectorMedia: selectorMedia[control.selectorMediaKey],
          surchargeCents: control.surchargeCents,
        },
        rules,
      );
      choice.querySelector('input').name = 'control';
      root.append(choice);
    });

    const chainContainer = section.querySelector('[data-chain-material-options]');
    section.querySelector('[data-chain-material-list]')?.replaceChildren();
    if (chainContainer) chainContainer.hidden = true;
    const safety = section.querySelector('[data-control-safety]');
    if (safety) {
      safety.replaceChildren();
      safety.hidden = true;
    }
  }

  function syncControls(form, rules, categoryApi, selections) {
    const available = new Set(
      categoryApi.availableControls(rules, selections).map((control) => control.id),
    );
    available.delete('pull_chain');
    available.delete('cord');
    const inputs = Array.from(form.querySelectorAll('[name="control"]'));
    const chainInputs = Array.from(form.querySelectorAll('[name="chain_material"]'));
    const retiredSelection = inputs.some((input) => input.checked && ['pull_chain', 'cord'].includes(input.value))
      || Boolean(selections.chain_material) || chainInputs.some((input) => input.checked);
    inputs.forEach((input) => {
      const isAvailable = available.has(input.value);
      if (retiredSelection) input.checked = isAvailable && input.value === 'cordless';
      input.disabled = !isAvailable;
      input.closest('.ndr-choice').hidden = !isAvailable;
      if (!isAvailable) input.checked = false;
    });
    const previewingMountControls = Boolean(rules.controlPreviewInstallation && !selections.headrail_style);
    if (!previewingMountControls && !inputs.some((input) => input.checked)) {
      const defaultControl = rules.defaultControlByInstallation?.[selections.installation_method];
      const preferred = inputs.find((input) => !input.disabled && input.value === defaultControl);
      const fallback = !rules.requireExplicitControlChoice ? inputs.find((input) => !input.disabled) : null;
      if (preferred || fallback) (preferred || fallback).checked = true;
    }
    const chainContainer = form.querySelector('[data-chain-material-options]');
    if (chainContainer) chainContainer.hidden = true;
    chainInputs.forEach((input) => { input.checked = false; input.disabled = true; });
    const safety = form.querySelector('[data-control-safety]');
    if (safety) safety.hidden = true;
  }

  function renderUpgrades(section, rules) {
    const root = section.querySelector('[data-upgrade-options]');
    if (!root) return;
    root.replaceChildren();
    const entries = Object.entries(rules.upgrades || {});
    if (entries.length === 0) return;
    entries.forEach(([id, upgrade]) => {
      const choice = makeChoice(
        {
          name: 'upgrades',
          value: id,
          labelText: upgrade.label,
          note: upgrade.note,
          type: 'checkbox',
          surchargeCents: upgrade.surchargeCents,
        },
        rules,
      );
      choice.querySelector('input').name = 'upgrades';
      choice.dataset.upgradeRow = id;
      root.append(choice);
    });
  }

  function renderAccessories(section, rules, selectorMedia) {
    const root = section.querySelector('[data-accessory-options]');
    if (!root) return;
    root.replaceChildren();
    const none = rules.accessoryNone || {
      id: 'none',
      label: 'No accessories',
      note: 'No add-ons chosen.',
    };
    root.append(makeChoice({
      name: 'accessory_choice',
      value: none.id,
      labelText: none.label,
      note: none.note,
      checked: true,
      selectorMedia: selectorMedia[none.selectorMediaKey],
    }, rules));
    Object.entries(rules.accessories || {}).forEach(([id, accessory]) => {
      root.append(makeChoice({
        name: 'accessory_choice',
        value: id,
        labelText: accessory.label,
        note: accessory.note,
        checked: false,
        selectorMedia: selectorMedia[accessory.selectorMediaKey],
        surchargeCents: accessory.surchargeCents,
      }, rules));
    });
  }

  function populateWholeSelect(select, minimum, maximum, preferred) {
    select.replaceChildren();
    for (let value = Math.floor(Number(minimum)); value <= Math.floor(Number(maximum)); value += 1) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = String(value);
      if (value === preferred) option.selected = true;
      select.append(option);
    }
  }

  // Default preview size: 36 x 60 in, kept inside the category's size lists.
  function previewDefaultSize(measurement, dimension) {
    return dimension === 'height'
      ? Math.min(Math.max(60, Math.ceil(measurement.minHeight)), Math.floor(measurement.maxHeight))
      : Math.min(Math.max(36, Math.ceil(measurement.minWidth)), Math.floor(measurement.maxWidth));
  }

  function populateMeasurements(form, rules) {
    if (rules.measurement?.status === 'unresolved') return;
    const defaultWidth = previewDefaultSize(rules.measurement, 'width');
    const defaultHeight = previewDefaultSize(rules.measurement, 'height');
    populateWholeSelect(form.elements.widthWhole, rules.measurement.minWidth, rules.measurement.maxWidth, defaultWidth);
    populateWholeSelect(form.elements.heightWhole, rules.measurement.minHeight, rules.measurement.maxHeight, defaultHeight);
    if (rules.steppedBuilder) {
      ['widthWhole', 'heightWhole'].forEach(name => {
        const input = form.elements[name];
        input.prepend(new Option('Choose', '')); input.value = '';
      });
    }
  }

  // The lists are the limits (NDR-D-093): the size lists only offer sizes the chosen options can make.
  // Out-of-range options are removed (iPhone Safari ignores hidden options). If a change makes the
  // chosen size unavailable, that choice is cleared ("Choose") so the customer picks again from the list.
  const SIZE_FRACTIONS = [[0, '0'], [0.125, '1/8'], [0.25, '1/4'], [0.375, '3/8'], [0.5, '1/2'], [0.625, '5/8'], [0.75, '3/4'], [0.875, '7/8']];

  function rebuildSizeSelect(select, values, labelFor) {
    const current = select.value;
    const cleared = current === '' || !values.some((value) => String(value) === current);
    const signature = `${cleared ? 'choose|' : ''}${values.join('|')}`;
    if (select.dataset.sizeChoices !== signature) {
      const options = values.map((value) => new Option(labelFor(value), String(value)));
      if (cleared) options.unshift(new Option('Choose', ''));
      select.replaceChildren(...options);
      select.dataset.sizeChoices = signature;
    }
    select.value = cleared ? '' : current;
  }

  function fractionLabel(value) {
    return (SIZE_FRACTIONS.find(([fraction]) => fraction === value) || [value, String(value)])[1];
  }

  function limitSizeChoices(form, bounds) {
    [['width', bounds.minWidth, bounds.maxWidth], ['height', bounds.minHeight, bounds.maxHeight]].forEach(([dimension, min, max]) => {
      const wholeSelect = form.elements[`${dimension}Whole`];
      const fractionSelect = form.elements[`${dimension}Fraction`];
      if (!wholeSelect || !fractionSelect || !Number.isFinite(min) || !Number.isFinite(max)) return;
      const wholes = [];
      for (let whole = Math.floor(min); whole <= Math.floor(max); whole += 1) wholes.push(whole);
      rebuildSizeSelect(wholeSelect, wholes, String);
      const whole = wholeSelect.value === '' ? NaN : Number(wholeSelect.value);
      const fractions = SIZE_FRACTIONS.map(([fraction]) => fraction)
        .filter((fraction) => !Number.isFinite(whole) || (whole + fraction >= min - 1e-9 && whole + fraction <= max + 1e-9));
      rebuildSizeSelect(fractionSelect, fractions, fractionLabel);
    });
  }

  // Saved or edited shades restore their sizes into the full list first; render() then narrows it.
  function resetSizeChoices(form, rules) {
    if (rules.measurement?.status === 'unresolved') return;
    ['width', 'height'].forEach((dimension) => {
      const key = dimension === 'width' ? 'Width' : 'Height';
      const min = Number(rules.measurement[`min${key}`]);
      const max = Number(rules.measurement[`max${key}`]);
      const wholes = [];
      for (let whole = Math.floor(min); whole <= Math.floor(max); whole += 1) wholes.push(whole);
      const wholeSelect = form.elements[`${dimension}Whole`];
      const fractionSelect = form.elements[`${dimension}Fraction`];
      wholeSelect.replaceChildren(...wholes.map((whole) => new Option(String(whole), String(whole))));
      fractionSelect.replaceChildren(...SIZE_FRACTIONS.map(([fraction, label]) => new Option(label, String(fraction))));
      delete wholeSelect.dataset.sizeChoices;
      delete fractionSelect.dataset.sizeChoices;
    });
  }

  const tapeFractionLabels = {
    0: '',
    0.125: '1/8',
    0.25: '1/4',
    0.375: '3/8',
    0.5: '1/2',
    0.625: '5/8',
    0.75: '3/4',
    0.875: '7/8',
  };

  function syncTapeMeasures(form) {
    ['width', 'height'].forEach((dimension) => {
      const tape = form.querySelector(`[data-tape-measure="${dimension}"]`);
      if (!tape) return;
      // Until a full size is picked (a list shows "Choose"), hide the tape so it never shows an old size.
      const empty = form.elements[`${dimension}Whole`].value === '' || form.elements[`${dimension}Fraction`].value === '';
      tape.style.display = empty ? 'none' : '';
      if (empty) {
        tape.querySelector('[data-tape-reading]').textContent = '';
        return;
      }
      const whole = Number(form.elements[`${dimension}Whole`].value);
      const fraction = Number(form.elements[`${dimension}Fraction`].value);
      const fractionIndex = Math.round(fraction * 8);
      const fractionLabel = tapeFractionLabels[fraction] || '';
      const reading = `${whole}${fractionLabel ? ` ${fractionLabel}` : ''} in`;
      tape.style.setProperty('--ndr-tape-position', `${fractionIndex * 12.5}%`);
      tape.dataset.fractionIndex = String(fractionIndex);
      tape.querySelector('[data-tape-start]').textContent = String(whole);
      tape.querySelector('[data-tape-end]').textContent = String(whole + 1);
      tape.querySelector('[data-tape-reading]').textContent = `Selected: ${reading}`;
      tape.setAttribute('aria-label', `Shade ${dimension} set to ${reading}. The tape shows 1/8-inch marks from ${whole} to ${whole + 1} inches.`);
    });
  }

  function selectedValue(form, name) {
    return form.querySelector(`[name="${name}"]:checked`)?.value || '';
  }

  function selectedOptions(form, rules) {
    const options = Object.fromEntries(
      rules.optionGroups.map((group) => [group.id, selectedValue(form, `option_${group.id}`)]),
    );
    options.chain_material = selectedValue(form, 'chain_material');
    return options;
  }

  const ASSET_MAP_KEYS = [
    'swatches',
    'weaveDetails',
    'exactScenes',
    'families',
    'referenceScenes',
    'reviewPresets',
    'exactControlLayers',
    'exactControlLayersByMount',
    'controlVisualAliases',
    'exactSceneLabels',
    'constructions',
    'constructionProfiles',
  ];

  function mergeVisualAssetMaps(maps) {
    const result = {};
    let referenceOwnerFamilyId = '';
    maps.forEach((map) => {
      if (!map || typeof map !== 'object') return;
      if (map.schemaVersion !== undefined && result.schemaVersion === undefined) result.schemaVersion = map.schemaVersion;
      if (map.defaultHardwareFinish && result.defaultHardwareFinish === undefined) result.defaultHardwareFinish = map.defaultHardwareFinish;
      if (map.defaultReferenceSceneKey && result.defaultReferenceSceneKey === undefined) result.defaultReferenceSceneKey = map.defaultReferenceSceneKey;
      ASSET_MAP_KEYS.forEach((key) => {
        const value = map[key];
        if (!value || typeof value !== 'object' || Array.isArray(value)) return;
        result[key] = { ...(result[key] || {}), ...value };
      });
      if (map.referenceScenes && typeof map.familyId === 'string') referenceOwnerFamilyId = map.familyId;
    });
    const firstFamilyId = maps.find((map) => map && typeof map.familyId === 'string')?.familyId || '';
    result.familyId = referenceOwnerFamilyId || firstFamilyId;
    return result;
  }

  function readProductVisualAssets(section) {
    const nodes = Array.from(section.querySelectorAll('[data-product-visual-assets]'));
    if (nodes.length === 0) return null;
    const maps = [];
    nodes.forEach((node) => {
      try {
        const parsed = JSON.parse(node.textContent);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) maps.push(parsed);
      } catch (error) {
        console.error('No Drill product visual assets could not be loaded.', error);
      }
    });
    if (maps.length === 0) return null;
    return maps.length === 1 ? maps[0] : mergeVisualAssetMaps(maps);
  }

  function applyProductSwatches(rules, visualAssets) {
    const swatches = visualAssets?.swatches || {};
    const weaveDetails = visualAssets?.weaveDetails || {};
    rules.optionGroups.forEach((group) => {
      group.options.forEach((option) => {
        if (swatches[option.id]) option.swatchImage = swatches[option.id];
        if (option.weaveDetailMediaKey && weaveDetails[option.weaveDetailMediaKey]) {
          option.weaveDetailImage = weaveDetails[option.weaveDetailMediaKey];
        }
      });
    });
  }

  function syncPreviewSwatchChip(chip, option) {
    if (!chip) return;
    chip.style.backgroundColor = option?.swatchHex || '#f5f0e8';
    chip.style.backgroundImage = option?.swatchImage ? `url("${option.swatchImage}")` : 'none';
    chip.hidden = !option?.swatchHex && !option?.swatchImage;
  }

  function restoreProductFallback(scene) {
    const fallbackSrc = scene.dataset.fallbackSrc;
    const fallbackAlt = scene.dataset.fallbackAlt || 'Window treatment preview';
    if (fallbackSrc && scene.getAttribute('src') !== fallbackSrc) scene.src = fallbackSrc;
    scene.alt = fallbackAlt;
    delete scene.dataset.baseVisualAlt;
  }

  function bindProductVisualPair(scene, hardware, stack, visualStatus, usesGovernedReference) {
    if (!scene || !hardware || !stack) return;
    const hideHardware = (visualized = true) => {
      hardware.hidden = true;
      hardware.removeAttribute('src');
      stack.dataset.visualized = visualized ? 'true' : 'false';
    };
    scene.addEventListener('error', () => {
      clearGeneratedVisualLayers(stack);
      if (stack.dataset.visualMode === 'installation-pending') {
        scene.removeAttribute('src');
        hideHardware(false);
        delete stack.dataset.visualReference;
        delete stack.dataset.visualKey;
        if (visualStatus) visualStatus.textContent = 'Choose how to mount your shade to start.';
        return;
      }
      restoreProductFallback(scene);
      if (!usesGovernedReference) {
        hideHardware(false);
        stack.dataset.visualMode = 'fallback-scene';
        delete stack.dataset.visualPresentation;
        delete stack.dataset.visualReference;
        delete stack.dataset.visualKey;
        if (visualStatus) visualStatus.textContent = 'Room idea. See your exact choices in the summary.';
        return;
      }
      hideHardware();
      stack.dataset.visualMode = 'reference-scene';
      stack.dataset.visualReference = 'governed';
      stack.dataset.visualPresentation = 'portrait-contain';
      delete stack.dataset.visualKey;
      if (visualStatus) visualStatus.textContent = 'This is an example shade. We do not have an image of your choices.';
    });
    hardware.addEventListener('error', () => {
      if (!usesGovernedReference) {
        hideHardware(false);
        scene.alt = scene.dataset.baseVisualAlt || scene.dataset.fallbackAlt || 'Window treatment preview';
        stack.dataset.visualMode = 'fallback-scene';
        delete stack.dataset.visualPresentation;
        delete stack.dataset.visualReference;
        delete stack.dataset.visualKey;
        if (visualStatus) visualStatus.textContent = 'Room idea. We do not have an image of your choices.';
        return;
      }
      hideHardware();
      scene.alt = scene.dataset.baseVisualAlt || scene.dataset.fallbackAlt || 'Example shade preview';
      stack.dataset.visualMode = 'reference-scene';
      stack.dataset.visualReference = 'retained';
      if (visualStatus) visualStatus.textContent = 'This is an example shade. We do not have an image of your choices.';
    });
  }

  function bindProductVisualErrorHandling(section, rules) {
    const usesGovernedReference = rules.mode === 'catalog-v2';
    bindProductVisualPair(
      section.querySelector('[data-product-preview-scene]'),
      section.querySelector('[data-product-preview-hardware]'),
      section.querySelector('[data-product-preview-stack]'),
      section.querySelector('[data-preview-visual-status]'),
      usesGovernedReference,
    );
    bindProductVisualPair(
      section.querySelector('[data-mobile-product-preview-scene]'),
      section.querySelector('[data-mobile-product-preview-hardware]'),
      section.querySelector('[data-mobile-product-preview-stack]'),
      section.querySelector('[data-mobile-preview-status]'),
      usesGovernedReference,
    );
    const heroImage = section.querySelector('[data-category-hero-image]');
    heroImage?.addEventListener('error', () => {
      restoreProductFallback(heroImage);
    });
  }

  function clearGeneratedVisualLayers(stack) {
    stack?.querySelectorAll('[data-product-preview-generated-layer]').forEach((layer) => layer.remove());
  }

  function makeGeneratedVisualLayer(layer, onError) {
    const image = document.createElement('img');
    image.className = 'ndr-summary__preview-hardware';
    image.dataset.productPreviewGeneratedLayer = layer.slot;
    image.src = layer.src;
    image.alt = '';
    image.decoding = 'async';
    image.style.clipPath = `inset(${layer.mask.top}% ${layer.mask.right}% ${layer.mask.bottom}% ${layer.mask.left}%)`;
    image.addEventListener('error', onError, { once: true });
    return image;
  }

  function appendGeneratedVisualLayers(stack, layers, onError) {
    (layers || []).forEach((layer) => stack.append(makeGeneratedVisualLayer(layer, onError)));
  }

  function preloadVisualSource(src) {
    return new Promise((resolve, reject) => {
      if (typeof src !== 'string' || !src) {
        reject(new Error('Missing product visual source.'));
        return;
      }
      const image = new Image();
      let settled = false;
      const fail = () => {
        if (settled) return;
        settled = true;
        reject(new Error(`Unable to load product visual source: ${src}`));
      };
      const ready = () => {
        if (settled) return;
        if (!image.naturalWidth || !image.naturalHeight) {
          fail();
          return;
        }
        const complete = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        if (typeof image.decode !== 'function') {
          complete();
          return;
        }
        image.decode().then(complete, fail);
      };
      image.addEventListener('load', ready, { once: true });
      image.addEventListener('error', fail, { once: true });
      image.decoding = 'async';
      image.src = src;
      if (image.complete) queueMicrotask(ready);
    });
  }

  function preloadResolvedVisual(resolved) {
    const sources = [resolved.baseSceneSrc || resolved.sceneSrc];
    if (Array.isArray(resolved.layers)) {
      resolved.layers.forEach((layer) => sources.push(layer.src));
    } else {
      sources.push(resolved.hardwareSrc);
    }
    return Promise.all([...new Set(sources)].map(preloadVisualSource));
  }

  function beginVisualRequest(section) {
    const requestId = (Number.parseInt(section.dataset.visualRequestId || '0', 10) || 0) + 1;
    section.dataset.visualRequestId = String(requestId);
    return requestId;
  }

  function isCurrentVisualRequest(section, requestId) {
    return section.dataset.visualRequestId === String(requestId);
  }

  function showProductVisualFallback(pairs, visualStatus, mobileVisualStatus, status) {
    pairs.forEach(({ scene, hardware, stack }) => {
      clearGeneratedVisualLayers(stack);
      restoreProductFallback(scene);
      hardware.hidden = true;
      hardware.removeAttribute('src');
      stack.dataset.visualized = 'false';
      stack.dataset.visualMode = 'fallback-scene';
      delete stack.dataset.visualPresentation;
      delete stack.dataset.visualReference;
      delete stack.dataset.visualKey;
    });
    if (visualStatus) visualStatus.textContent = status;
    if (mobileVisualStatus) mobileVisualStatus.textContent = status;
  }

  function markRetainedProductReference(pairs, visualStatus, mobileVisualStatus) {
    pairs.forEach(({ scene, stack }) => {
      if (!scene.getAttribute('src')) return;
      scene.alt = scene.dataset.baseVisualAlt || scene.alt || 'Example shade preview';
      stack.dataset.visualized = 'true';
      stack.dataset.visualMode = 'reference-scene';
      stack.dataset.visualReference = 'retained';
      if (!stack.dataset.visualPresentation) stack.dataset.visualPresentation = 'portrait-contain';
    });
    const status = 'This is an example shade. We do not have an image of your choices.';
    if (visualStatus) visualStatus.textContent = status;
    if (mobileVisualStatus) mobileVisualStatus.textContent = status;
  }

  function commitReferenceVisual(
    pairs,
    resolved,
    visualStatus,
    mobileVisualStatus,
    status = 'This is an example shade. We do not have an image of your choices.',
  ) {
    pairs.forEach(({ scene, hardware, stack }) => {
      clearGeneratedVisualLayers(stack);
      scene.src = resolved.baseSceneSrc || resolved.sceneSrc;
      scene.dataset.baseVisualAlt = 'Example shade preview';
      scene.alt = 'Example shade preview';
      hardware.hidden = true;
      hardware.removeAttribute('src');
      stack.dataset.visualized = 'true';
      stack.dataset.visualMode = 'reference-scene';
      stack.dataset.visualReference = 'governed';
      stack.dataset.visualPresentation = resolved.presentationMode || 'portrait-contain';
      delete stack.dataset.visualKey;
    });
    if (visualStatus) visualStatus.textContent = status;
    if (mobileVisualStatus) mobileVisualStatus.textContent = status;
  }

  function showProductVisualReference(
    pairs,
    resolved,
    visualStatus,
    mobileVisualStatus,
    section,
    requestId,
  ) {
    const hasRetainableProductVisual = pairs.every(({ scene, stack }) => (
      Boolean(scene.getAttribute('src')) && stack.dataset.visualized === 'true'
    ));
    if (hasRetainableProductVisual) {
      if (!isCurrentVisualRequest(section, requestId)) return Promise.resolve('stale');
      markRetainedProductReference(pairs, visualStatus, mobileVisualStatus);
      if (!resolved) return Promise.resolve('retained');
    }
    if (!resolved) return Promise.resolve(null);
    return preloadResolvedVisual(resolved)
      .then(() => {
        if (!isCurrentVisualRequest(section, requestId)) return 'stale';
        commitReferenceVisual(pairs, resolved, visualStatus, mobileVisualStatus);
        return 'governed';
      })
      .catch((error) => {
        if (!isCurrentVisualRequest(section, requestId)) return 'stale';
        console.error('No Drill product reference visual could not be loaded.', error);
        return null;
      });
  }

  function resolvedVisualKey(resolved) {
    const layers = Array.isArray(resolved?.layers)
      ? resolved.layers.map((layer) => [
        layer.slot || '',
        layer.src || '',
        layer.mask?.top ?? '',
        layer.mask?.right ?? '',
        layer.mask?.bottom ?? '',
        layer.mask?.left ?? '',
      ].join(':'))
      : [];
    return [
      resolved?.visualMode || '',
      resolved?.exactKey || '',
      resolved?.baseSceneSrc || resolved?.sceneSrc || '',
      resolved?.hardwareSrc || '',
      ...layers,
    ].join('::');
  }

  function commitResolvedVisual(
    pairs,
    resolved,
    baseVisualAlt,
    visualAlt,
    visualStatus,
    mobileVisualStatus,
    section,
    requestId,
    reference,
    referenceReady,
  ) {
    const visualKey = resolvedVisualKey(resolved);
    pairs.forEach(({ scene, hardware, stack }) => {
      clearGeneratedVisualLayers(stack);
      scene.src = resolved.baseSceneSrc || resolved.sceneSrc;
      scene.dataset.baseVisualAlt = baseVisualAlt;
      scene.alt = visualAlt;
      stack.dataset.visualMode = resolved.visualMode;
      stack.dataset.visualKey = visualKey;
      delete stack.dataset.visualReference;
      if (resolved.presentationMode) {
        stack.dataset.visualPresentation = resolved.presentationMode;
      } else {
        delete stack.dataset.visualPresentation;
      }
      if (Array.isArray(resolved.layers)) {
        hardware.hidden = true;
        hardware.removeAttribute('src');
        appendGeneratedVisualLayers(stack, resolved.layers, () => {
          if (!isCurrentVisualRequest(section, requestId)
            || stack.dataset.visualKey !== visualKey) return;
          const status = 'This is an example shade. We do not have an image of your choices.';
          if (resolved.baseSceneRole === 'construction-free') {
            if (reference && referenceReady) {
              commitReferenceVisual(pairs, reference, visualStatus, mobileVisualStatus, status);
            }
            return;
          }
          pairs.forEach(({ scene: pairedScene, hardware: pairedHardware, stack: pairedStack }) => {
            if (pairedStack.dataset.visualKey !== visualKey) return;
            clearGeneratedVisualLayers(pairedStack);
            pairedScene.alt = pairedScene.dataset.baseVisualAlt
              || pairedScene.alt
              || 'Example shade preview';
            pairedHardware.hidden = true;
            pairedHardware.removeAttribute('src');
            pairedStack.dataset.visualized = 'true';
            pairedStack.dataset.visualMode = 'reference-scene';
            pairedStack.dataset.visualReference = 'retained';
            pairedStack.dataset.visualPresentation = 'portrait-contain';
            delete pairedStack.dataset.visualKey;
          });
          if (visualStatus) visualStatus.textContent = status;
          if (mobileVisualStatus) mobileVisualStatus.textContent = status;
        });
      } else {
        hardware.src = resolved.hardwareSrc;
        hardware.alt = '';
        hardware.hidden = false;
        stack.dataset.visualMode = 'legacy';
      }
      stack.dataset.visualized = 'true';
    });
  }

  function showInstallationFirstVisual(pairs, baseSceneSrc, visualStatus, mobileVisualStatus) {
    const status = 'Choose how to mount your shade to start.';
    pairs.forEach(({ scene, hardware, stack }) => {
      clearGeneratedVisualLayers(stack);
      if (baseSceneSrc) scene.src = baseSceneSrc;
      else scene.removeAttribute('src');
      scene.alt = 'Bare window before you choose a mount';
      delete scene.dataset.baseVisualAlt;
      hardware.hidden = true;
      hardware.removeAttribute('src');
      stack.dataset.visualized = baseSceneSrc ? 'true' : 'false';
      stack.dataset.visualMode = 'installation-pending';
      stack.dataset.visualPresentation = 'portrait-contain';
      delete stack.dataset.visualKey;
      delete stack.dataset.visualReference;
    });
    if (visualStatus) visualStatus.textContent = status;
    if (mobileVisualStatus) mobileVisualStatus.textContent = status;
    return { visualMode: 'installation-pending', baseSceneSrc: baseSceneSrc || '' };
  }

  function syncProductVisualization(section, selections, rules, categoryApi, visualAssets) {
    const pairs = [
      {
        scene: section.querySelector('[data-product-preview-scene]'),
        hardware: section.querySelector('[data-product-preview-hardware]'),
        stack: section.querySelector('[data-product-preview-stack]'),
      },
      {
        scene: section.querySelector('[data-mobile-product-preview-scene]'),
        hardware: section.querySelector('[data-mobile-product-preview-hardware]'),
        stack: section.querySelector('[data-mobile-product-preview-stack]'),
      },
    ].filter((pair) => pair.scene && pair.hardware && pair.stack);
    const mobilePreview = section.querySelector('[data-mobile-live-preview]');
    const hardwareSummary = section.querySelector('[data-preview-hardware-summary]');
    const hardwareLabels = section.querySelectorAll('[data-preview-hardware-label]');
    const mobileHardwareLabel = section.querySelector('[data-mobile-preview-hardware]');
    const visualStatus = section.querySelector('[data-preview-visual-status]');
    const mobileVisualStatus = section.querySelector('[data-mobile-preview-status]');
    if (!pairs.length) return null;
    const requestId = beginVisualRequest(section);
    if (rules.requireExplicitInstallationChoice && !selections.installation_method) {
      const baseSceneSrc = visualAssets?.families?.[selections.fabric_family]?.baseSceneSrc || '';
      const pendingVisual = showInstallationFirstVisual(pairs, baseSceneSrc, visualStatus, mobileVisualStatus);
      if (mobilePreview) mobilePreview.hidden = false;
      return pendingVisual;
    }
    if (rules.mediaStatus === 'placeholder') {
      pairs.forEach(({ scene, hardware, stack }) => {
        clearGeneratedVisualLayers(stack);
        restoreProductFallback(scene);
        hardware.hidden = true;
        hardware.removeAttribute('src');
        stack.dataset.visualized = 'false';
      });
      const placeholderStatus = 'No product image yet.';
      if (visualStatus) visualStatus.textContent = placeholderStatus;
      if (mobileVisualStatus) mobileVisualStatus.textContent = placeholderStatus;
      if (mobilePreview) mobilePreview.hidden = true;
      return null;
    }

    const finishGroup = rules.optionGroups.find((group) => group.id === 'hardware_finish');
    const finishActive = Boolean(finishGroup && categoryApi.isGroupActive(finishGroup, selections));
    if (hardwareSummary) hardwareSummary.hidden = !finishActive;
    const finishId = finishActive ? selections.hardware_finish : '';
    const finish = finishGroup?.options.find((option) => option.id === finishId);
    hardwareLabels.forEach((label) => { label.textContent = finish?.label || 'Choose a finish'; });
    if (mobileHardwareLabel) mobileHardwareLabel.textContent = finish?.label || 'Choose a hardware color';

    const usesGovernedReference = rules.mode === 'catalog-v2';
    const resolved = globalThis.NoDrillVisualization
      ? globalThis.NoDrillVisualization.resolveVisual(selections, visualAssets)
        || (usesGovernedReference ? globalThis.NoDrillVisualization.resolveProgressiveVisual?.(selections, visualAssets) : null)
      : null;

    const fabricGroup = rules.optionGroups.find((group) => group.id === 'fabric_color');
    const fabric = fabricGroup?.options.find((option) => option.id === selections.fabric_color);
    const familyGroup = rules.optionGroups.find((group) => group.id === 'fabric_family');
    const family = familyGroup?.options.find((option) => option.id === selections.fabric_family);
    const headrailGroup = rules.optionGroups.find((group) => group.id === 'headrail_style');
    const headrail = headrailGroup?.options.find((option) => option.id === selections.headrail_style);
    const productLabel = family?.label || resolved?.constructionLabel || 'Window treatment';
    const baseVisualAlt = `${productLabel} ${fabric?.label || ''} with ${headrail?.label || 'selected headrail'} in a finished room`.replace(/\s+/g, ' ').trim();
    const visualAlt = `${productLabel} ${fabric?.label || ''} with ${headrail?.label || 'selected headrail'}${finish?.label ? ` and ${finish.label} hardware` : ''} in a finished room`.replace(/\s+/g, ' ').trim();

    if (usesGovernedReference) {
      const reference = globalThis.NoDrillVisualization?.resolveReferenceVisual?.(selections, visualAssets) || null;
      if (!resolved) {
        showProductVisualReference(pairs, reference, visualStatus, mobileVisualStatus, section, requestId);
        const mobileFabricLabel = section.querySelector('[data-mobile-preview-fabric]');
        if (mobileFabricLabel) mobileFabricLabel.textContent = fabric?.label || 'Choose a fabric color';
        if (mobilePreview) mobilePreview.hidden = false;
        return null;
      }
      const resolvedKey = resolvedVisualKey(resolved);
      if (pairs.some(({ stack }) => stack.dataset.visualKey !== resolvedKey)) {
        markRetainedProductReference(pairs, visualStatus, mobileVisualStatus);
      }
      const referenceReady = reference
        ? preloadResolvedVisual(reference).then(() => true, () => false)
        : Promise.resolve(false);
      Promise.all([preloadResolvedVisual(resolved), referenceReady])
        .then(([, isReferenceReady]) => {
          if (!isCurrentVisualRequest(section, requestId)) return;
          if (resolved.baseSceneRole === 'construction-free' && !isReferenceReady) {
            console.error('No Drill governed construction reference could not be loaded.');
            return;
          }
          commitResolvedVisual(pairs, resolved, baseVisualAlt, visualAlt, visualStatus, mobileVisualStatus, section, requestId, reference, isReferenceReady);
          const mobileFabricLabel = section.querySelector('[data-mobile-preview-fabric]');
          if (mobileFabricLabel) mobileFabricLabel.textContent = fabric?.label || 'Choose a fabric color';
          if (mobilePreview) mobilePreview.hidden = false;
          const pendingConstructionLabel = installationLabel(rules, selections.installation_method, selections.installation_method === 'standard-bracket'
            ? 'Standard Bracket'
            : 'No-Drill');
          const status = ['provisional-exact-scene', 'provisional-layered-scene'].includes(resolved.visualMode)
            ? `${pendingConstructionLabel} preview. Choose a control.`
            : 'Preview of your exact choices';
          if (visualStatus) visualStatus.textContent = status;
          if (mobileVisualStatus) mobileVisualStatus.textContent = status;
        })
        .catch((error) => {
          if (!isCurrentVisualRequest(section, requestId)) return;
          console.error('No Drill selected-combination visual could not be loaded.', error);
          showProductVisualReference(pairs, reference, visualStatus, mobileVisualStatus, section, requestId);
          if (mobilePreview) mobilePreview.hidden = false;
        });
      return resolved;
    }

    if (!resolved) {
      if (!usesGovernedReference) {
        const hasHeadrailGroup = rules.optionGroups.some((group) => group.id === 'headrail_style');
        const fallbackStatus = selections.headrail_style
          ? 'Room idea. See your exact choices in the summary.'
          : hasHeadrailGroup
            ? 'Room idea. Choose a headrail to go on.'
            : 'Room idea. See your choices in the summary.';
        showProductVisualFallback(pairs, visualStatus, mobileVisualStatus, fallbackStatus);
        if (mobilePreview) mobilePreview.hidden = true;
        return null;
      }
      showProductVisualReference(
        pairs,
        reference,
        visualStatus,
        mobileVisualStatus,
        section,
        requestId,
      );
      if (mobilePreview) mobilePreview.hidden = false;
      return null;
    }

    pairs.forEach(({ scene, hardware, stack }) => {
      clearGeneratedVisualLayers(stack);
      scene.src = resolved.baseSceneSrc || resolved.sceneSrc;
      scene.dataset.baseVisualAlt = baseVisualAlt;
      scene.alt = visualAlt;
      if (Array.isArray(resolved.layers)) {
        hardware.hidden = true;
        hardware.removeAttribute('src');
        appendGeneratedVisualLayers(stack, resolved.layers, () => {
          clearGeneratedVisualLayers(stack);
          restoreProductFallback(scene);
          stack.dataset.visualized = 'false';
          stack.dataset.visualMode = 'fallback';
          if (visualStatus) visualStatus.textContent = 'Room idea. We do not have an image of your choices.';
        });
        stack.dataset.visualMode = 'layered';
      } else {
        hardware.src = resolved.hardwareSrc;
        hardware.alt = '';
        hardware.hidden = false;
        stack.dataset.visualMode = 'legacy';
      }
      stack.dataset.visualized = 'true';
    });
    const mobileFabricLabel = section.querySelector('[data-mobile-preview-fabric]');
    if (mobileFabricLabel) mobileFabricLabel.textContent = fabric?.label || 'Choose a fabric color';
    if (mobilePreview) mobilePreview.hidden = false;
    if (visualStatus) visualStatus.textContent = 'Preview of your exact choices';
    return resolved;
  }

  function readPageLocks(section) {
    const root = section || globalThis.document;
    const bootstrap = typeof root?.querySelector === 'function' ? root.querySelector('[data-configurator-bootstrap]') : null;
    return bootstrap ? (JSON.parse(bootstrap.textContent).pageLocks || {}) : {};
  }

  function readBuilderParams() {
    const params = new URLSearchParams(globalThis.location.search);
    const pageLocks = readPageLocks();
    ['fabric_family', 'lock_fabric_family', 'fabric_type', 'lock_fabric_type'].forEach((key) => {
      if (typeof pageLocks[key] === 'string' && pageLocks[key]) params.set(key, pageLocks[key]);
    });
    return params;
  }

  function readQueryLocks(rules) {
    const params = readBuilderParams();
    const locks = {};
    rules.optionGroups.forEach((group) => {
      const requested = params.get(`lock_${group.id}`);
      const requestedOption = group.options.find((option) => option.id === requested);
      if (requestedOption && requestedOption.configurationEligible !== false) locks[group.id] = requested;
    });

    const familyGroup = rules.optionGroups.find((group) => group.id === 'fabric_family');
    const lockedFamily = familyGroup?.options.find((option) => option.id === locks.fabric_family);
    const configurationUnproven = lockedFamily?.configurationEligible !== true;
    const blockedByEligibility = rules.mode === 'catalog-v2'
      ? configurationUnproven
      : lockedFamily?.configurationEligible === false;
    if (locks.fabric_family && blockedByEligibility) delete locks.fabric_family;
    Object.entries(lockedFamily?.requires || {}).forEach(([groupId, allowed]) => {
      if (allowed.length === 1) locks[groupId] = allowed[0];
    });
    if (rules.id === 'roller-shades' && locks.installation_method === 'no-drill') {
      locks.mount_location = 'inside';
    }
    return locks;
  }

  function readFamilyPolicyContext(rules, categoryApi) {
    const params = readBuilderParams();
    return categoryApi.resolveFamilyPolicyContext?.(rules, [
      ...params.getAll('lock_fabric_family'),
      ...params.getAll('fabric_family'),
    ]) || {};
  }

  function enforceQueryLocks(form, locks) {
    Object.entries(locks || {}).forEach(([groupId, value]) => {
      const noDrillInsideInvariant = groupId === 'mount_location'
        && value === 'outside'
        && selectedValue(form, 'option_installation_method') === 'no-drill';
      if (noDrillInsideInvariant) return;
      form.querySelectorAll(`[name="option_${groupId}"]`).forEach((input) => {
        input.checked = input.value === value;
      });
    });
  }

  function normalizeQueryLocks(form, rules, categoryApi, locks) {
    if (!locks || typeof locks !== 'object') return;
    for (let pass = 0; pass <= rules.optionGroups.length; pass += 1) {
      enforceQueryLocks(form, locks);
      const selections = selectedOptions(form, rules);
      let changed = false;
      Object.entries(locks).forEach(([groupId, value]) => {
        const group = rules.optionGroups.find((candidate) => candidate.id === groupId);
        const active = group && categoryApi.isGroupActive(group, selections);
        const available = active && categoryApi.availableOptions(group, selections).some((option) => option.id === value);
        if (!available) {
          delete locks[groupId];
          changed = true;
        }
      });
      if (!changed) return;
    }
  }

  function syncOptionDependencies(form, rules, categoryApi, locks) {
    normalizeQueryLocks(form, rules, categoryApi, locks);
    enforceQueryLocks(form, locks);
    for (let pass = 0; pass <= rules.optionGroups.length; pass += 1) {
      const selections = selectedOptions(form, rules);
      let changed = false;
      rules.optionGroups.forEach((group) => {
        const availableIds = new Set(categoryApi.availableOptions(group, selections).map((option) => option.id));
        form.querySelectorAll(`[name="option_${group.id}"]`).forEach((input) => {
          if (input.checked && !availableIds.has(input.value)) {
            input.checked = false;
            changed = true;
          }
        });
      });
      if (!changed) break;
    }

    const selections = selectedOptions(form, rules);
    rules.optionGroups.forEach((group) => {
      const wrapper = form.querySelector(`[data-option-group="${group.id}"]`);
      const active = categoryApi.isGroupActive(group, selections);
      const availableIds = new Set(categoryApi.availableOptions(group, selections).map((option) => option.id));
      const lockedValue = locks?.[group.id];
      const collapseSingle = group.hideWhenSingleOption && availableIds.size === 1;
      const derivedForSelection = Boolean(
        group.hideWhenDerived
        && group.derivedFrom
        && (!group.derivedWhen || Object.entries(group.derivedWhen).every(
          ([optionId, allowed]) => Array.isArray(allowed) && allowed.includes(selections[optionId]),
        )),
      );
      if (wrapper) {
        if (lockedValue) {
          wrapper.hidden = true;
          wrapper.dataset.optionLocked = 'true';
        } else {
          wrapper.hidden = !active || collapseSingle || derivedForSelection;
          wrapper.dataset.optionDerived = derivedForSelection ? 'true' : 'false';
        }
      }
      const inputs = Array.from(form.querySelectorAll(`[name="option_${group.id}"]`));
      inputs.forEach((input) => {
        const available = active && availableIds.has(input.value);
        input.disabled = Boolean(lockedValue) || !available;
        const choice = input.closest('.ndr-choice');
        choice.hidden = !available;
        const selectorMediaByHeadrail = input.dataset.selectorMediaByHeadrail
          ? JSON.parse(input.dataset.selectorMediaByHeadrail)
          : {};
        const selectedMedia = selectorMediaByHeadrail[selections.headrail_style];
        const mediaImage = choice.querySelector('.ndr-choice__media-image');
        const colorOnly = group.colorOnlyHeadrails?.includes(selections.headrail_style) === true;
        if (group.colorOnlyHeadrails) {
          choice.classList.toggle('ndr-choice--color-only', colorOnly);
          choice.classList.toggle('ndr-choice--media', Boolean(mediaImage) && !colorOnly);
          const media = choice.querySelector('.ndr-choice__media');
          if (media) media.hidden = colorOnly;
        }
        if (colorOnly && mediaImage) {
          mediaImage.removeAttribute('src');
          mediaImage.hidden = true;
        } else if (selectedMedia && mediaImage && mediaImage.src !== selectedMedia) {
          mediaImage.src = selectedMedia;
          mediaImage.hidden = false;
          mediaImage.closest('.ndr-choice__media')?.classList.remove('is-unavailable');
        }
        const weaveChoice = choice.closest('[data-weave-choice]');
        if (weaveChoice) weaveChoice.hidden = !available;
      });
      const defaultOption = categoryApi.defaultOption(group, selections);
      if (active && defaultOption && !inputs.some((input) => input.checked)) {
        const defaultInput = inputs.find((input) => input.value === defaultOption && !input.disabled);
        if (defaultInput) defaultInput.checked = true;
      }
      const waiting = wrapper?.querySelector('[data-option-waiting]');
      if (waiting) waiting.hidden = !active || availableIds.size > 0;
    });
  }

  function setOptionValue(form, groupId, value) {
    form.querySelectorAll(`[name="option_${groupId}"]`).forEach((input) => {
      input.checked = input.value === value;
    });
  }

  function syncDerivedBottomRailFinish(form, rules, categoryApi, core) {
    const group = rules.optionGroups.find((candidate) => candidate.id === 'bottom_rail_finish');
    if (!group?.derivedFrom || !core?.deriveBottomRailFinish) return '';
    const selections = selectedOptions(form, rules);
    const derived = core.deriveBottomRailFinish(selections, rules);
    if (derived === null) {
      const retained = selections.bottom_rail_finish || '';
      if (retained) return retained;
      const availableFinishes = categoryApi.availableOptions(group, selections);
      const matchingFinish = availableFinishes.find((option) => option.id === selections.hardware_finish);
      const defaultFinish = matchingFinish?.id || availableFinishes[0]?.id || '';
      if (defaultFinish) setOptionValue(form, 'bottom_rail_finish', defaultFinish);
      return defaultFinish;
    }
    if (selections.bottom_rail_finish !== derived) setOptionValue(form, 'bottom_rail_finish', derived);
    return derived;
  }

  function syncDerivedOptionSelections(form, rules, categoryApi, core) {
    rules.optionGroups.forEach((group) => {
      if (group.id === 'bottom_rail_finish') return;
      const selections = selectedOptions(form, rules);
      const derived = categoryApi.derivedOption?.(group, selections);
      if (derived == null || selections[group.id] === derived) return;
      setOptionValue(form, group.id, derived);
    });
    syncDerivedBottomRailFinish(form, rules, categoryApi, core);
  }

  function stabilizeOptionSelections(form, rules, categoryApi, locks, core) {
    for (let pass = 0; pass <= rules.optionGroups.length; pass += 1) {
      const before = JSON.stringify(selectedOptions(form, rules));
      if (rules.preserveCompatibleInstallationSelections) syncDerivedOptionSelections(form, rules, categoryApi, core);
      syncOptionDependencies(form, rules, categoryApi, locks);
      syncDerivedOptionSelections(form, rules, categoryApi, core);
      const after = selectedOptions(form, rules);
      if (JSON.stringify(after) === before) return after;
    }
    return selectedOptions(form, rules);
  }

  function reconcileInstallationMountState(form, rules, core, previousOptions) {
    if (rules.id !== 'roller-shades') return selectedOptions(form, rules);
    const requested = selectedOptions(form, rules);
    const transition = core.reconcileInstallationMountTransition(previousOptions || {}, requested);
    if (transition.options.mount_location !== requested.mount_location) {
      setOptionValue(form, 'mount_location', transition.options.mount_location);
    }
    if (transition.installationChanged && !rules.preserveCompatibleInstallationSelections) {
      ['headrail_style', 'hardware_finish', 'bottom_rail', 'bottom_rail_finish'].forEach((groupId) => {
        setOptionValue(form, groupId, '');
      });
      form.querySelectorAll('[name="control"], [name="accessory_choice"], [name="upgrades"]').forEach((input) => {
        input.checked = input.name === 'accessory_choice' && input.value === 'none';
      });
    }
    if (transition.mountChanged && form.elements.measurementConfirmed) {
      form.elements.measurementConfirmed.checked = false;
    }
    if (transition.mountChanged && form.elements.noDrillDepthConfirmed) {
      form.elements.noDrillDepthConfirmed.checked = false;
    }
    return transition.options;
  }

  function syncInstallationFirstPresentation(section, rules, selections) {
    if (rules.steppedBuilder) return false;
    if (!rules.requireExplicitInstallationChoice) return false;
    const pending = !selections.installation_method;
    const waiting = section.querySelector('[data-installation-first-waiting]');
    const mountNote = section.querySelector('[data-installation-mount-note]');
    const mobilePreview = section.querySelector('[data-mobile-live-preview]');
    const installationStep = section.querySelector('[data-flow-stage="installation_mount"]');
    if (waiting) {
      waiting.textContent = rules.installationFirstWaitingLabel || 'Choose how to mount your shade to start.';
      waiting.hidden = !pending;
    }
    if (pending && mountNote) mountNote.hidden = true;
    const syncPendingVisibility = (node) => {
      if (!node) return;
      if (pending) {
        if (!Object.prototype.hasOwnProperty.call(node.dataset, 'installationFirstPreviousHidden')) {
          node.dataset.installationFirstPreviousHidden = node.hidden ? 'true' : 'false';
        }
        node.hidden = true;
        node.dataset.installationFirstPending = 'true';
      } else if (Object.prototype.hasOwnProperty.call(node.dataset, 'installationFirstPreviousHidden')) {
        node.hidden = node.dataset.installationFirstPreviousHidden === 'true';
        delete node.dataset.installationFirstPreviousHidden;
        delete node.dataset.installationFirstPending;
      }
    };
    if (pending && mobilePreview && installationStep && mobilePreview.parentElement !== installationStep) {
      mobilePreview.installationFirstParent = mobilePreview.parentElement;
      mobilePreview.installationFirstNextSibling = mobilePreview.nextSibling;
      installationStep.append(mobilePreview);
    } else if (!pending && mobilePreview?.installationFirstParent) {
      mobilePreview.installationFirstParent.insertBefore(
        mobilePreview,
        mobilePreview.installationFirstNextSibling || null,
      );
      delete mobilePreview.installationFirstParent;
      delete mobilePreview.installationFirstNextSibling;
    }
    section.querySelectorAll('[data-configurator-form] .ndr-step[data-flow-stage]').forEach((step) => {
      if (step.dataset.flowStage !== 'installation_mount') syncPendingVisibility(step);
    });
    syncPendingVisibility(section.querySelector('.ndr-summary__details'));
    syncPendingVisibility(section.querySelector('[data-configuration-check]'));
    ['[data-preview-swatch]', '.ndr-summary__preview-price', '[data-mobile-preview-fabric]', '[data-mobile-preview-hardware]']
      .forEach((selector) => syncPendingVisibility(section.querySelector(selector)));
    return pending;
  }

  function syncInstallationMountPresentation(section, rules, selections, core) {
    const noDrill = selections.installation_method === 'no-drill';
    const depthConfirmation = section.querySelector('[data-no-drill-depth-confirmation]');
    const depthInput = section.querySelector('[name="noDrillDepthConfirmed"]');
    const depthHelp = section.querySelector('[data-no-drill-depth-help]');
    const mountSystem = resolveMountSystem(selections, rules.defaultMount);
    const mount = rules.mounts[mountSystem];
    const depthRequired = core.requiresNoDrillInsideDepth({
      options: selections,
      mountSystem,
      mountType: selections.mount_location || mount?.allowedMountTypes?.[0] || '',
    });
    const legacyMountStep = section.querySelector('[data-legacy-mount-step]');
    const canonicalDepthStep = section.querySelector('[data-flow-stage="installation_mount"]');
    const depthStep = Array.isArray(rules.customizerFlow) ? canonicalDepthStep : legacyMountStep;
    if (depthConfirmation && depthStep && depthConfirmation.parentElement !== depthStep) depthStep.append(depthConfirmation);
    if (depthConfirmation) depthConfirmation.hidden = !depthRequired;
    if (depthHelp) {
      depthHelp.textContent = rules.noDrillDepthHelp || '';
      depthHelp.hidden = !depthRequired || !rules.noDrillDepthHelp;
    }
    if (!depthRequired && depthInput) depthInput.checked = false;
    if (rules.id !== 'roller-shades') return;
    const mountGroup = section.querySelector('[data-option-group="mount_location"]');
    const mountRules = rules.optionGroups.find((group) => group.id === 'mount_location');
    const derivedMount = Boolean(
      mountRules?.hideWhenDerived
      && mountRules.derivedFrom
      && (!mountRules.derivedWhen || Object.entries(mountRules.derivedWhen).every(
        ([optionId, allowed]) => Array.isArray(allowed) && allowed.includes(selections[optionId]),
      ))
      && mountRules.derivedOptionMap?.[selections[mountRules.derivedFrom]],
    );
    const installationRoot = section.querySelector('[data-product-option-stage="installation"]');
    if (mountGroup && installationRoot && mountGroup.parentElement !== installationRoot) installationRoot.append(mountGroup);
    const standard = selections.installation_method === 'standard-bracket';
    if (mountGroup) mountGroup.hidden = derivedMount || !standard;
    const note = section.querySelector('[data-installation-mount-note]');
    if (note) {
      note.hidden = derivedMount;
      if (!derivedMount) {
        note.textContent = noDrill
          ? 'Inside mount only. No separate mount choice is needed.'
          : standard
            ? (selections.mount_location
                ? `Standard Bracket Mount · ${selections.mount_location === 'inside' ? 'Inside Mount' : 'Outside Mount'}`
                : 'Pick Inside Mount or Outside Mount to finish Step 1.')
            : 'Choose how to mount your shade to see how it works.';
      }
    }
  }

  function syncFinishCoordinationWarning(section, configuration, rules, core) {
    const warning = core.getFinishCoordinationWarning(configuration.options, rules);
    const root = section.querySelector('[data-finish-coordination-warning]');
    const copy = section.querySelector('[data-finish-warning-copy]');
    if (!root || !copy) return warning;
    const nextHidden = !warning.active;
    const nextCopy = warning.active
      ? `Your Headrail is ${warning.headrailFinishLabel}. Your Bottom Rail is ${warning.bottomRailFinishLabel}. These colors will not match.`
      : '';
    if (root.hidden !== nextHidden) root.hidden = nextHidden;
    if (copy.textContent !== nextCopy) copy.textContent = nextCopy;
    return warning;
  }

  function placeMobilePreview(section) {
    // Phones: the markup puts the live preview right after the mount step, as a direct child of the form,
    // so it stays pinned (CSS sticky) through every later step. applyCustomizerFlowOrder re-appends the
    // steps, so put the preview back after the mount step.
    const form = section.querySelector('[data-configurator-form]');
    const card = form?.querySelector('[data-mobile-live-preview]');
    const mountStep = form?.querySelector(':scope > .ndr-step[data-flow-stage="installation_mount"]');
    if (!card || !mountStep) return;
    form.insertBefore(card, mountStep.nextSibling);
  }

  function setupMobilePreviewSpacing(section) {
    // Steps and fields scrolled into view must land below the pinned preview, not under it.
    const form = section.querySelector('[data-configurator-form]');
    const card = section.querySelector('[data-mobile-live-preview]');
    if (!form || !card || typeof ResizeObserver !== 'function') return;
    const update = () => {
      const height = card.getBoundingClientRect().height;
      if (height > 0) form.style.setProperty('--ndr-mobile-preview-space', `${Math.ceil(height + 24)}px`);
      else form.style.removeProperty('--ndr-mobile-preview-space');
    };
    new ResizeObserver(update).observe(card);
    update();
  }

  function setupMobilePreviewZoom(section) {
    const card = section.querySelector('[data-mobile-live-preview]');
    const stack = card?.querySelector('[data-mobile-product-preview-stack]');
    if (!card || !stack || typeof document.createElement('dialog').showModal !== 'function') return;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'ndr-mobile-live-preview__zoom';
    trigger.setAttribute('aria-label', 'See a bigger picture of your shade');
    trigger.innerHTML = '<span aria-hidden="true"><svg viewBox="0 0 24 24" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h6v6M10 20H4v-6M20 4l-6 6M4 20l6-6"/></svg></span>';
    stack.after(trigger);

    const dialog = document.createElement('dialog');
    const close = document.createElement('button');
    const frame = document.createElement('div');
    dialog.className = 'ndr-preview-zoom';
    dialog.setAttribute('aria-label', 'Your shade');
    close.type = 'button';
    close.className = 'ndr-preview-zoom__close';
    close.textContent = 'Close';
    frame.className = 'ndr-preview-zoom__frame';
    dialog.append(close, frame);
    document.body.append(dialog);

    trigger.addEventListener('click', () => {
      const box = stack.getBoundingClientRect();
      const sourceImages = Array.from(stack.querySelectorAll('img'));
      const copy = stack.cloneNode(true);
      // The copy must never be found by the live preview code, so it keeps no data hooks or ids.
      [copy, ...copy.querySelectorAll('*')].forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-') || attribute.name === 'id') node.removeAttribute(attribute.name);
        });
      });
      copy.querySelectorAll('button').forEach((button) => button.remove());
      copy.className = 'ndr-preview-zoom__stack';
      if (box.width > 0 && box.height > 0) {
        // Keep the exact shape of the small preview so every layer stays lined up, and fit it on screen.
        copy.style.aspectRatio = `${box.width} / ${box.height}`;
        copy.style.width = `min(100%, calc((92vh - 5rem) * ${(box.width / box.height).toFixed(4)}))`;
      }
      copy.querySelectorAll('img').forEach((image, index) => {
        if (sourceImages[index]) image.style.objectFit = getComputedStyle(sourceImages[index]).objectFit;
      });
      frame.replaceChildren(copy);
      dialog.showModal();
    });
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === frame) dialog.close();
    });
    dialog.addEventListener('close', () => {
      frame.replaceChildren();
      trigger.focus({ preventScroll: true });
    });
  }

  function preferredScrollBehavior() {
    return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  function focusStage(section, stage) {
    const fieldset = section.querySelector(`[data-flow-stage="${stage}"]`);
    if (!fieldset) return;
    fieldset.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
    const target = Array.from(fieldset.querySelectorAll('input:not([disabled]), button:not([disabled]), select:not([disabled])'))
      .find((control) => control.getClientRects().length > 0);
    target?.focus({ preventScroll: true });
  }

  function updateProgress(section, configuration, validation, rules, core) {
    const selected = configuration.options || {};
    const measurementRules = core.resolveMeasurementRules(configuration, rules);
    const complete = new Set();
    const matchesSelected = (conditions) => !conditions || Object.entries(conditions).every(
      ([id, allowed]) => allowed.includes(selected[id]),
    );
    const noDrillDepthRequired = core.requiresNoDrillInsideDepth(configuration);
    if (noDrillDepthRequired && configuration.noDrillDepthConfirmed) {
      complete.add('installation_mount');
      complete.add('installation_method');
      complete.add('mount_location');
    }
    if (selected.installation_method === 'standard-bracket' && ['inside', 'outside'].includes(selected.mount_location)) complete.add('installation_mount');
    if (selected.fabric_color) complete.add('fabric_color');
    if (selected.headrail_style) complete.add('headrail_style');
    if (selected.hardware_finish) complete.add('hardware_finish');
    if (configuration.measurementConfirmed
      && rules.measurement?.status !== 'unresolved'
      && core.validateMeasurement(configuration.width, 'width', measurementRules).valid
      && core.validateMeasurement(configuration.height, 'height', measurementRules).valid) complete.add('measurements');
    if (configuration.control && core.validateRollerAddons(configuration, rules).valid) complete.add('control_method');
    const bottomRailFinish = (rules.optionGroups || []).find((group) => group.id === 'bottom_rail_finish');
    const bottomRailFinishRequired = Boolean(bottomRailFinish && (
      bottomRailFinish.required || matchesSelected(bottomRailFinish.requiredWhen)
    ));
    const bottomRailFinishValid = (bottomRailFinish?.options || []).some((option) => (
      option.id === selected.bottom_rail_finish && matchesSelected(option.requires)
    ));
    if (selected.bottom_rail && (!bottomRailFinishRequired || bottomRailFinishValid)) complete.add('bottom_rail');
    if (configuration.accessoryChoice) complete.add('accessories');
    if (configuration.room && configuration.roomLabel && configuration.compatibilityConfirmed) complete.add('window_details');
    if (rules.id === 'cellular-shades' && complete.has('window_details')) complete.add('remaining_details');

    const steps = Array.from(section.querySelectorAll('.ndr-step')).filter((step) => !step.hidden);
    const orderedStages = [...steps.map((step) => step.dataset.flowStage || ''), 'review'];
    const items = Array.from(section.querySelectorAll('[data-configurator-progress] [data-progress-stage]'));
    const firstIncompleteIndex = orderedStages.findIndex((stage) => (
      stage === 'review' ? !validation.valid : !complete.has(stage)
    ));
    const activeIndex = firstIncompleteIndex === -1 ? orderedStages.length - 1 : firstIncompleteIndex;

    items.forEach((item, index) => {
      const stage = item.dataset.progressStage || orderedStages[index] || '';
      const isComplete = stage === 'review' ? validation.valid : complete.has(stage);
      const isActive = index === activeIndex;
      const isProductionSizingPending = stage === 'measurements' && rules.measurement?.status === 'unresolved';
      const status = item.querySelector('[data-progress-status]');
      item.classList.toggle('is-complete', isComplete);
      item.classList.toggle('is-active', isActive);
      if (isActive) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
      if (status) {
        if (isProductionSizingPending) status.textContent = isActive ? 'This step: no sizes yet' : 'No sizes yet';
        else if (isActive && isComplete) status.textContent = 'Current · Ready';
        else if (isActive) status.textContent = 'This step: choose an option';
        else if (isComplete) status.textContent = '✓ Complete';
        else status.textContent = 'Choose an option';
      }
    });

    const summary = section.querySelector('[data-configuration-check-summary]');
    const completedCount = items.filter((item) => item.classList.contains('is-complete')).length;
    if (summary) {
      summary.textContent = validation.valid
        ? 'You have made all required choices. Check your shade setup before you go on.'
        : `${completedCount} of ${items.length} checks done. Choose a missing item to go back to it.`;
    }
  }

  function applyQuerySelection(form, rules, categoryApi, locks) {
    const params = readBuilderParams();
    rules.optionGroups.forEach((group) => {
      const requested = params.get(group.id);
      if (!requested || !group.options.some((option) => option.id === requested)) return;
      syncOptionDependencies(form, rules, categoryApi, locks);
      const selections = selectedOptions(form, rules);
      const availableIds = new Set(categoryApi.availableOptions(group, selections).map((option) => option.id));
      if (!availableIds.has(requested)) return;
      const input = Array.from(form.querySelectorAll(`[name="option_${group.id}"]`))
        .find((candidate) => candidate.value === requested);
      if (input && (!input.disabled || locks?.[group.id] === requested)) input.checked = true;
    });
    syncOptionDependencies(form, rules, categoryApi, locks);
    const requested = normalizeRestoredControl({
      control: params.get('control') || selectedValue(form, 'control'),
      options: { ...selectedOptions(form, rules), chain_material: params.get('chain_material') || '' },
    });
    form.querySelectorAll('[name="control"]').forEach((input) => { input.checked = input.value === requested.control; });
    syncControls(form, rules, categoryApi, requested.options);
  }

  function applyExactSceneReviewPreset(form, rules, categoryApi, locks, visualAssets) {
    const requestedFinish = new URLSearchParams(globalThis.location.search).get('review_exact_scene') || '';
    const resolvedPreset = globalThis.NoDrillVisualization?.resolveExactSceneReviewPreset?.(
      requestedFinish,
      locks,
      visualAssets,
    );
    if (!resolvedPreset) return null;
    const preset = normalizeRestoredControl(resolvedPreset);

    Object.entries(preset.options).forEach(([groupId, value]) => {
      if (groupId !== 'chain_material') setOptionValue(form, groupId, value);
    });
    syncOptionDependencies(form, rules, categoryApi, locks);
    const selections = selectedOptions(form, rules);
    syncControls(form, rules, categoryApi, selections);
    form.querySelectorAll('[name="control"]').forEach((input) => {
      input.checked = input.value === preset.control;
    });
    syncControls(form, rules, categoryApi, selections);
    form.querySelectorAll('[name="accessory_choice"]').forEach((input) => {
      input.checked = input.value === preset.accessory;
    });
    return preset;
  }

  function mountingSurfaceLabel(value) {
    if (value === 'flat') return 'Flat / smooth';
    if (value === 'textured') return 'Textured / uneven';
    return 'Choose a surface';
  }

  function resolveMountSystem(options, legacyMountSystem) {
    if (options?.installation_method === 'no-drill' && options.mount_location === 'inside') {
      return 'no-drill-inside';
    }
    if (options?.installation_method === 'standard-bracket' && options.mount_location === 'inside') {
      return 'standard-inside';
    }
    if (options?.installation_method === 'standard-bracket' && options.mount_location === 'outside') {
      return 'standard-outside';
    }
    return legacyMountSystem || '';
  }

  function syncMeasurementGuidance(section, form, selections, rules, core) {
    const mountLocation = selections.mount_location || 'inside';
    const outside = mountLocation === 'outside';
    const insideGuidance = section.querySelector('[data-inside-measure-guidance]');
    const outsideGuidance = section.querySelector('[data-outside-measure-guidance]');
    const heading = section.querySelector('[data-measurement-step-heading]');
    const lead = section.querySelector('[data-measurement-step-lead]');
    const confirmationCopy = section.querySelector('[data-measurement-confirmation-copy]');
    const widthCopy = section.querySelector('[data-width-measure-copy]');
    const heightCopy = section.querySelector('[data-height-measure-copy]');
    if (heading && rules.previewMeasurementDefaults) heading.textContent = 'Shade size';
    // Outside mount orders the finished shade size, so the stepped heading must not ask for the window size.
    if (heading && rules.steppedBuilder) heading.textContent = outside ? 'What size shade do you need?' : 'What size is your window?';
    if (rules.measurement?.status === 'unresolved') {
      if (insideGuidance) insideGuidance.hidden = false;
      if (outsideGuidance) outsideGuidance.hidden = true;
      if (lead) lead.textContent = 'Sizing is not available for this shade yet.';
      form.dataset.measurementMountLocation = mountLocation;
      return;
    }
    if (insideGuidance) insideGuidance.hidden = outside;
    if (outsideGuidance) outsideGuidance.hidden = !outside;
    if (widthCopy) widthCopy.textContent = outside ? 'Finished shade width' : 'Smallest of top, middle, and bottom';
    if (heightCopy) heightCopy.textContent = outside ? 'Finished shade height' : 'Exact opening height';
    // Customers pick sizes from lists, so the copy says "pick".
    if (lead) {
      lead.textContent = outside
        ? 'For outside mount, pick the size of the finished shade. Do not take anything off the sizes you measured.'
        : 'Pick the exact size inside the opening. Do not take anything off.';
    }
    if (confirmationCopy) {
      confirmationCopy.textContent = outside
        ? 'I picked the finished size for outside mount. I checked what it will cover and that there is room to mount it.'
        : 'I measured the width at the top, middle and bottom and picked the smallest of the three. I did not round.';
    }
    const previous = form.dataset.measurementMountLocation;
    if (previous && previous !== mountLocation) form.elements.measurementConfirmed.checked = false;
    form.dataset.measurementMountLocation = mountLocation;
  }

  // A cleared size ("Choose") reads as no size, never as 0.
  function chosenSize(form, dimension) {
    const whole = form.elements[`${dimension}Whole`].value;
    const fraction = form.elements[`${dimension}Fraction`].value;
    return whole === '' || fraction === '' ? NaN : Number(whole) + Number(fraction);
  }

  function readConfiguration(form, rules) {
    const options = {};
    rules.optionGroups.forEach((group) => {
      options[group.id] = selectedValue(form, `option_${group.id}`);
    });
    options.chain_material = selectedValue(form, 'chain_material');
    const normalized = normalizeRestoredControl({ control: selectedValue(form, 'control'), options });
    const control = normalized.control;
    options.chain_material = '';
    const legacyMountSystem = form.elements.mountSystem?.value || '';
    const mountSystem = resolveMountSystem(options, legacyMountSystem);
    const mount = rules.mounts[mountSystem];
    const measurementPending = rules.measurement?.status === 'unresolved';
    return {
      options,
      width: measurementPending ? null : chosenSize(form, 'width'),
      height: measurementPending ? null : chosenSize(form, 'height'),
      mountingSurface: selectedValue(form, 'mountingSurface'),
      openingMaterial: form.elements.openingMaterial.value,
      openingMaterialCautionAcknowledged: Boolean(form.elements.openingMaterialCautionAcknowledged.checked),
      noDrillDepthConfirmed: Boolean(form.elements.noDrillDepthConfirmed?.checked),
      room: form.elements.room.value,
      roomLabel: form.elements.roomLabel.value.trim(),
      ...(rules.steppedBuilder ? {autoWindowName: !form.elements.roomLabel.value.trim()} : {}),
      mountSystem,
      mountType: options.mount_location || mount?.allowedMountTypes?.[0] || '',
      control,
      ...(control === 'motorized'
        ? { controlSide: selectedValue(form, 'controlSide') === 'left' ? 'left' : 'right' }
        : {}),
      ...(rules.mode === 'catalog-v2' && rules.id === 'roller-shades' ? {
        sideChannel: selectedValue(form, 'sideChannel') || 'none',
        remoteType: selectedValue(form, 'remoteType'),
        remoteQuantity: Number(form.elements.remoteQuantity?.value ?? 1),
        compatibleRemoteAcknowledgement: Boolean(form.elements.compatibleRemoteAcknowledged?.checked),
        orderAccessoryQuantities: {
          one_channel_remote: Number(form.elements.oneChannelRemoteQuantity?.value || 0),
          six_channel_remote: Number(form.elements.sixChannelRemoteQuantity?.value || 0),
          sixteen_channel_remote: Number(form.elements.sixteenChannelRemoteQuantity?.value || 0),
          compatible_remote_acknowledged: Boolean(form.elements.compatibleRemoteAcknowledged?.checked),
        },
      } : {}),
      upgrades: Array.from(form.querySelectorAll('[name="upgrades"]:checked')).map((input) => input.value),
      accessoryChoice: selectedValue(form, 'accessory_choice') || 'none',
      accessories: selectedValue(form, 'accessory_choice') && selectedValue(form, 'accessory_choice') !== 'none'
        ? [selectedValue(form, 'accessory_choice')]
        : [],
      accessoryQuantities: {},
      measurementConfirmed: measurementPending ? false : Boolean(form.elements.measurementConfirmed?.checked),
      compatibilityConfirmed: Boolean(form.elements.compatibilityConfirmed.checked),
    };
  }

  function optionSummary(configuration, rules, categoryApi) {
    const summary = rules.optionGroups
      .filter((group) => !group.hideFromSummary && categoryApi.isGroupActive(group, configuration.options))
      .map((group) => {
        const option = group.options.find((candidate) => candidate.id === configuration.options[group.id]);
        return { id: group.id, label: group.label, value: option?.label || 'Choose an option' };
      });
    return summary;
  }

  function installationLabel(rules, method, fallback) {
    if (!rules.useInstallationOptionLabels) return fallback;
    const installation = rules.optionGroups.find((group) => group.id === 'installation_method');
    return installation?.options.find((option) => option.id === method)?.label || fallback;
  }

  function depthSummaryRows(configuration, rules, core) {
    const required = core.requiresNoDrillInsideDepth(configuration);
    if (!required && rules.hideInapplicableDepthSummary) return [];
    return [[
      '2¾-inch mounting depth',
      required ? (configuration.noDrillDepthConfirmed ? 'Confirmed' : 'Required') : 'Not required',
    ]];
  }

  function syncControlSide(section, form, configuration) {
    const applicable = configuration.control === 'motorized';
    const group = section.querySelector('[data-control-side-options]');
    if (group) group.hidden = !applicable;
    const side = applicable && configuration.controlSide === 'left' ? 'left' : 'right';
    form.querySelectorAll('[name="controlSide"]').forEach((input) => {
      input.disabled = !applicable;
      input.checked = input.value === side;
    });
  }

  function syncRollerAddons(section, form, rules, configuration, core) {
    const enabled = rules.mode === 'catalog-v2' && rules.id === 'roller-shades';
    const channelsAllowed = core.sideChannelsAvailable(configuration, rules);
    const channels = section.querySelector('[data-side-channel-options]');
    if (channels) channels.hidden = !channelsAllowed;
    form.querySelectorAll('[name="sideChannel"]').forEach((input) => {
      input.disabled = !channelsAllowed;
      if (!channelsAllowed) input.checked = input.value === 'none';
    });
    const motorized = enabled && configuration.control === 'motorized';
    const remotes = section.querySelector('[data-motorized-remote-options]');
    if (remotes) remotes.hidden = !motorized;
    form.querySelectorAll('[name="remoteType"]').forEach((input) => {
      input.disabled = !motorized;
      if (!motorized) input.checked = false;
    });
    const paidRemote = motorized && ['one_channel', 'six_channel', 'sixteen_channel'].includes(configuration.remoteType);
    const noRemote = motorized && configuration.remoteType === 'none';
    const quantity = section.querySelector('[data-remote-quantity]');
    if (quantity) quantity.hidden = !paidRemote;
    if (form.elements.remoteQuantity) {
      form.elements.remoteQuantity.disabled = !paidRemote;
      if (!motorized) form.elements.remoteQuantity.value = '1';
    }
    const acknowledgement = section.querySelector('[data-compatible-remote-acknowledgement]');
    if (acknowledgement) acknowledgement.hidden = !noRemote;
    if (form.elements.compatibleRemoteAcknowledged) {
      form.elements.compatibleRemoteAcknowledged.disabled = !noRemote;
      if (!noRemote) form.elements.compatibleRemoteAcknowledged.checked = false;
    }
    // Keep the prior order-accessory fields synchronized, not independent choices.
    Object.entries({ one_channel: 'oneChannelRemoteQuantity', six_channel: 'sixChannelRemoteQuantity', sixteen_channel: 'sixteenChannelRemoteQuantity' }).forEach(([type, name]) => {
      if (form.elements[name]) form.elements[name].value = paidRemote && configuration.remoteType === type
        ? String(configuration.remoteQuantity) : '0';
    });
    const cartNote = section.querySelector('[data-cart-remote-note]');
    if (cartNote) cartNote.hidden = !motorized || section.dataset.cartHasRemote !== 'true';
  }

  function priceWindowAddons(configuration, shadePrice, pricingApi, core, rules) {
    if (!pricingApi) return null;
    const sideChannel = configuration.sideChannel || 'none';
    const remoteType = configuration.control === 'motorized' ? configuration.remoteType : 'none';
    const channels = sideChannel === 'none' ? { valid: true, totalCents: 0 }
      : pricingApi.priceSideChannel?.({ height: configuration.height, sideChannel, fabricFamily: configuration.options.fabric_family });
    const remotes = remoteType === 'none' ? { valid: true, totalCents: 0 }
      : remoteType ? pricingApi.priceRemote?.({ type: remoteType, quantity: configuration.remoteQuantity }) : null;
    const valid = Boolean(shadePrice?.valid && channels?.valid && remotes?.valid
      && core.validateRollerAddons(configuration, rules).valid);
    return {
      valid,
      shadeCents: shadePrice?.valid ? shadePrice.totalCents : null,
      sideChannelCents: channels?.valid ? channels.totalCents : null,
      remoteCents: remotes?.valid ? remotes.totalCents : null,
      totalCents: valid ? shadePrice.totalCents + channels.totalCents + remotes.totalCents : null,
    };
  }

  function syncAddonPriceLabels(section, configuration, pricingApi, heightEntered) {
    section.querySelectorAll('[data-side-channel-price]').forEach((badge) => {
      const sideChannel = badge.dataset.sideChannelPrice;
      const price = heightEntered ? pricingApi.priceSideChannel?.({
        height: configuration.height, sideChannel, fabricFamily: configuration.options.fabric_family,
      }) : null;
      badge.textContent = heightEntered
        ? price?.valid ? `+${formatMoney(price.totalCents, 'USD')}` : 'Price pending'
        : sideChannel === 'l_channel' ? 'From $35' : 'From $39';
    });
    section.querySelectorAll('[data-remote-price]').forEach((badge) => {
      const price = pricingApi.priceRemote?.({ type: badge.dataset.remotePrice, quantity: 1 });
      badge.textContent = price?.valid ? `+${formatMoney(price.totalCents, 'USD')}` : 'Price pending';
    });
  }

  function syncCompatibility(form, rules, configuration) {
    form.querySelectorAll('[name="upgrades"]').forEach((input) => {
      const upgrade = rules.upgrades[input.value];
      const controlAllowed = !upgrade.requiresControls || upgrade.requiresControls.includes(configuration.control);
      const mountAllowed = !upgrade.allowedMounts || upgrade.allowedMounts.includes(configuration.mountSystem);
      input.disabled = !(controlAllowed && mountAllowed);
      if (input.disabled) input.checked = false;
      input.closest('.ndr-choice')?.classList.toggle('is-disabled', input.disabled);
    });
    form.querySelectorAll('[name="accessory_choice"]').forEach((input) => {
      if (input.value === 'none') return;
      const accessory = rules.accessories?.[input.value];
      const controlAllowed = !accessory?.requiresControls || accessory.requiresControls.includes(configuration.control);
      const mountAllowed = !accessory?.allowedMounts || accessory.allowedMounts.includes(configuration.mountSystem);
      input.disabled = !accessory || !(controlAllowed && mountAllowed);
      if (input.disabled) input.checked = false;
      input.closest('.ndr-choice')?.classList.toggle('is-disabled', input.disabled);
    });
    if (!form.querySelector('[name="accessory_choice"]:checked')) {
      const none = form.querySelector('[name="accessory_choice"][value="none"]');
      if (none) none.checked = true;
    }
  }

  function applyCategoryPresentation(section, rules, locks, selectorMedia) {
    section.classList.toggle('ndr-configurator--catalog-v2', rules.mode === 'catalog-v2');
    section.classList.toggle('ndr-configurator--catalog-only', rules.configurationEnabled === false);
    const drillMounted = rules.mountValidation.status === 'documented-drilled-only';
    const lockedLabel = rules.lockedOptionLabels?.fabric_type?.[locks?.fabric_type];
    const familyGroup = rules.optionGroups.find((group) => group.id === 'fabric_family');
    const lockedFamily = familyGroup?.options.find((option) => option.id === locks?.fabric_family);
    const familyRoomSource = lockedFamily?.roomMediaKey
      ? selectorMedia[lockedFamily.roomMediaKey]
      : '';
    rules.displayLabel = lockedLabel || rules.label;
    const heroImage = section.querySelector('[data-category-hero-image]');
    const heroLabel = section.querySelector('[data-category-hero-label]');
    const categoryImage = section.querySelector('[data-category-image]');
    const heroByCategory = {
      'cellular-shades': [heroImage.dataset.cellularSrc, 'Cellular shade example'],
      'zebra-shades': [heroImage.dataset.zebraSrc, 'Zebra shade example'],
      'shangri-la-sheer-shades': [heroImage.dataset.shangriLaSrc, 'Sheer Shade example'],
      'roller-shades': [heroImage.dataset.rollerSrc, 'Roller shade example'],
    };
    const [categorySource, categoryLabel] = heroByCategory[rules.id] || heroByCategory['roller-shades'];
    const source = familyRoomSource || categorySource;
    const label = familyRoomSource ? `${lockedFamily.label} room preview` : categoryLabel;
    section.querySelector('[data-category-heading]').textContent = rules.displayLabel;
    section.querySelector('[data-category-breadcrumb]').textContent = rules.displayLabel;
    section.querySelector('[data-summary-category]').textContent = rules.displayLabel;
    section.querySelector('[data-category-validation]').textContent = rules.validationLabel;
    const productHeading = section.querySelector('[data-product-heading]');
    const productLede = section.querySelector('[data-product-lede]');
    const changeCollection = section.querySelector('[data-change-collection]');
    const categoryPages = {
      'light-filtering': '/pages/light-filtering-roller-shades',
      'blackout': '/pages/blackout-roller-shades',
      'solar': '/pages/solar-roller-shades'
    };
    const categoryPage = rules.id === 'roller-shades' ? categoryPages[locks.fabric_type] : null;
    const catalogBreadcrumb = section.querySelector('.ndr-configurator__breadcrumb a');
    if (categoryPage && catalogBreadcrumb) catalogBreadcrumb.href = categoryPage;
    if (lockedFamily) {
      if (productHeading) productHeading.textContent = readPageLocks(section).title || lockedFamily.label;
      if (productLede) {
        productLede.textContent = rules.measurement?.status === 'unresolved'
          ? `Build your ${lockedFamily.label}. Pick a color, a control and hardware that go together. Sizes are not available for this shade yet.`
          : `Build your ${lockedFamily.label} from ${rules.displayLabel}. Choose a color, size and control. Then pick options that work with it.`;
      }
      if (changeCollection) {
        changeCollection.textContent = '← Change fabric collection';
        let changeUrl = new URL(changeCollection.href, globalThis.location.href);
        const contextGroup = locks.fabric_type ? 'fabric_type' : (locks.light_control ? 'light_control' : '');
        if (contextGroup) changeUrl.searchParams.set(contextGroup, locks[contextGroup]);
        if (rules.id === 'roller-shades' && contextGroup) {
          changeUrl.searchParams.set(`lock_${contextGroup}`, locks[contextGroup]);
        }
        if (categoryPage) changeUrl = new URL(categoryPage, globalThis.location.href);
        changeCollection.href = `${changeUrl.pathname}${changeUrl.search}${changeUrl.hash}`;
        changeCollection.hidden = false;
      }
    }
    heroImage.src = source;
    heroImage.dataset.fallbackSrc = source;
    const mediaPlaceholder = rules.mediaStatus === 'placeholder';
    const catalogReference = rules.mediaStatus === 'catalog-reference';
    const roomAlt = familyRoomSource ? `${lockedFamily.label} installed in a finished room` : '';
    heroImage.dataset.fallbackAlt = roomAlt || (mediaPlaceholder
      ? 'Example shade image'
      : (catalogReference ? `${rules.displayLabel} example` : `${rules.displayLabel} example in a finished room`));
    heroImage.alt = heroImage.dataset.fallbackAlt;
    heroLabel.textContent = familyRoomSource
      ? label
      : (mediaPlaceholder
      ? 'No product image yet'
      : (catalogReference ? `${rules.displayLabel} example` : label));
    categoryImage.src = source;
    categoryImage.dataset.fallbackSrc = source;
    categoryImage.dataset.fallbackAlt = roomAlt || (mediaPlaceholder
      ? 'Example shade image'
      : (catalogReference ? `${rules.displayLabel} example` : `${rules.displayLabel} fabric preview`));
    categoryImage.alt = categoryImage.dataset.fallbackAlt;
    const mobileScene = section.querySelector('[data-mobile-product-preview-scene]');
    if (mobileScene) {
      mobileScene.src = source;
      mobileScene.dataset.fallbackSrc = source;
      mobileScene.dataset.fallbackAlt = categoryImage.dataset.fallbackAlt;
      mobileScene.alt = categoryImage.dataset.fallbackAlt;
    }

    const noDrillOnly = section.querySelector('[data-no-drill-only]');
    const drillOnly = section.querySelector('[data-drill-only]');
    const mountFact = section.querySelector('[data-mount-fact]');
    const mountHeading = section.querySelector('[data-mount-step-heading]');
    const mountLead = section.querySelector('[data-mount-step-lead]');
    const mountingPointHelp = section.querySelector('[data-mounting-point-help]');
    const rollerFamilyFlow = Array.isArray(rules.customizerFlow);
    if (noDrillOnly) noDrillOnly.hidden = rollerFamilyFlow || drillMounted;
    if (drillOnly) drillOnly.hidden = rollerFamilyFlow || !drillMounted;
    if (mountFact) mountFact.textContent = rollerFamilyFlow
      ? 'Choose how to mount'
      : (drillMounted ? 'Traditional drill mount' : 'Inside-mount only');
    if (mountHeading) mountHeading.textContent = rollerFamilyFlow
      ? 'Choose mount location'
      : (drillMounted ? 'Confirm traditional mount' : 'Confirm no-drill inside mount');
    if (mountLead) mountLead.textContent = rollerFamilyFlow
      ? (rules.mode === 'catalog-v2'
        ? 'No-Drill is inside mount only. Standard brackets mount outside.'
        : 'No-Drill is inside mount only. Standard brackets can go inside or outside.')
      : (drillMounted
        ? 'This shade goes inside the opening. Brackets screw in to hold it.'
        : 'The no-drill parts fit inside the window opening.');
    if (mountingPointHelp) mountingPointHelp.textContent = rollerFamilyFlow
      ? 'Pick the material the hardware will attach to or touch.'
      : (drillMounted
        ? 'Pick what the inside of the opening is made of. You will screw the brackets into it.'
        : 'The no-drill hardware presses on the inside of the opening. Pick the surface it will press on.');

    const optionHeading = section.querySelector('[data-option-step-heading]');
    const optionLead = section.querySelector('[data-option-step-lead]');
    if (lockedFamily) {
      if (optionHeading) optionHeading.textContent = 'Choose fabric color';
      if (optionLead) optionLead.textContent = `Choose a color for ${lockedFamily.label}.`;
    } else if (lockedLabel) {
      if (optionHeading) optionHeading.textContent = 'Choose your fabric collection';
      if (optionLead) optionLead.textContent = `Pick a fabric collection for ${lockedLabel}.`;
    }
    const upgradeHeading = section.querySelector('[data-upgrade-step-heading]');
    const upgradeLead = section.querySelector('[data-upgrade-step-lead]');
    if (Object.keys(rules.upgrades || {}).length === 0) {
      if (upgradeHeading) upgradeHeading.textContent = rules.id === 'roller-shades' ? 'Window Details' : 'Review and confirm';
      if (upgradeLead) upgradeLead.textContent = rules.id === 'roller-shades'
        ? 'Name this window and confirm how it will mount.'
        : 'Check what the mount needs before you add this window.';
    }
  }

  function setupCatalogOnly(section, form, rules, categoryApi, locks) {
    renderOptionGroups(section, rules);
    applyQuerySelection(form, rules, categoryApi, locks);
    section.querySelector('.ndr-summary__details > .ndr-configurator__eyebrow').textContent = 'Your fabric choice';
    const summaryList = section.querySelector('[data-configuration-summary]');
    const previewSwatch = section.querySelector('[data-preview-swatch]');
    const previewSwatchChip = section.querySelector('[data-preview-swatch-chip]');
    const previewSwatchLabel = section.querySelector('[data-preview-swatch-label]');

    function renderCatalogSelection() {
      syncOptionDependencies(form, rules, categoryApi, locks);
      const selections = selectedOptions(form, rules);
      const options = optionSummary({ options: selections }, rules, categoryApi);
      summaryList.replaceChildren();
      [['Category', rules.displayLabel], ...options.map((option) => [option.label, option.value])].forEach(([label, value]) => {
        const term = document.createElement('dt');
        term.textContent = label;
        const detail = document.createElement('dd');
        detail.textContent = value;
        summaryList.append(term, detail);
      });
      const colorGroup = rules.optionGroups.find((group) => group.id === 'fabric_color');
      const color = colorGroup?.options.find((option) => option.id === selections.fabric_color);
      syncPreviewSwatchChip(previewSwatchChip, color);
      previewSwatchLabel.textContent = color?.label || 'Choose a color';
      previewSwatch.dataset.hasSelection = color ? 'true' : 'false';
      section.dataset.configuration = JSON.stringify({ categoryId: rules.id, options: selections, catalogOnly: true });
    }

    form.addEventListener('change', renderCatalogSelection);
    renderCatalogSelection();
  }

  function setupStandardBracketDialog(section) {
    const dialog = section.querySelector('[data-standard-bracket-dialog]');
    const close = dialog?.querySelector('[data-standard-bracket-dialog-close]');
    if (!dialog || !close) return () => {};

    const closeDialog = () => {
      dialog.hidden = true;
    };
    close.addEventListener('click', closeDialog);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    section.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !dialog.hidden) closeDialog();
    });

    return () => {
      if (globalThis.sessionStorage?.getItem(STANDARD_BRACKET_NOTICE_STORAGE_KEY)) return;
      globalThis.sessionStorage?.setItem(STANDARD_BRACKET_NOTICE_STORAGE_KEY, 'seen');
      dialog.hidden = false;
      close.focus({ preventScroll: true });
    };
  }

  function bluewaterStartingEstimate(configuration, estimatedPricing) {
    const options = configuration?.options || {};
    if (configuration?.control || options.fabric_family !== estimatedPricing?.profile?.familyId) return null;
    const control = 'cordless';
    const estimate = estimatedPricing?.priceConfiguration?.({
      ...configuration,
      control,
      options: {
        ...options,
        chain_material: '',
      },
    });
    return estimate?.valid && estimate.applicable ? { ...estimate, starting: true } : null;
  }

  function setup(section) {
    const core = globalThis.NoDrillConfiguratorCore;
    const categoryApi = globalThis.NoDrillCategoryConfig;
    const form = section.querySelector('[data-configurator-form]');
    const bootstrap = section.querySelector('[data-configurator-bootstrap]');
    if (!core || !categoryApi || !form || !bootstrap) return;

    let bootstrapData;
    let rules;
    try {
      bootstrapData = JSON.parse(bootstrap.textContent);
      const baseRules = categoryApi.buildRules(bootstrapData.categoryKey);
      rules = categoryApi.buildRules(bootstrapData.categoryKey, readFamilyPolicyContext(baseRules, categoryApi));
    } catch (error) {
      console.error('No Drill category configuration could not be loaded.', error);
      return;
    }

    const visualAssets = readProductVisualAssets(section);
    const selectorMedia = readSelectorMedia(section);
    applyProductSwatches(rules, visualAssets);
    const locks = readQueryLocks(rules);
    applyCategoryPresentation(section, rules, locks, selectorMedia);
    bindProductVisualErrorHandling(section, rules);
    if (rules.configurationEnabled === false) {
      setupCatalogOnly(section, form, rules, categoryApi, locks);
      return;
    }

    applyCatalogV2ReviewLayout(section, rules);
    setupCatalogV2PreviewRail(section, rules);
    renderOptionGroups(section, rules, selectorMedia);
    setupWeaveDetailPreviews(section);
    renderMounts(section, rules);
    renderControls(section, rules, selectorMedia);
    renderUpgrades(section, rules);
    renderAccessories(section, rules, selectorMedia);
    const installationStep = section.querySelector('[data-flow-stage="installation_mount"]');
    const legacyMountStep = section.querySelector('[data-legacy-mount-step]');
    const canonicalAccessories = section.querySelector('[data-canonical-roller-only]');
    const windowDetailsStep = section.querySelector('[data-window-details]');
    const mountDetailFields = section.querySelector('[data-mount-detail-fields]');
    const legacyFlow = section.querySelector('[data-legacy-flow-only]');
    const canonicalFlow = Array.isArray(rules.customizerFlow) && rules.customizerFlow.includes('installation_mount');
    if (canonicalFlow) {
      if (installationStep) installationStep.dataset.flowStage = 'installation_mount';
      if (legacyMountStep) {
        legacyMountStep.hidden = true;
        legacyMountStep.removeAttribute('data-flow-stage');
      }
      if (canonicalAccessories) canonicalAccessories.hidden = !rules.customizerFlow.includes('accessories');
      if (windowDetailsStep && !rules.steppedBuilder) windowDetailsStep.dataset.flowStage = 'window_details';
      // The markup already puts the surface and material questions in Window details for this flow.
      if (legacyFlow) legacyFlow.hidden = true;
    } else {
      if (installationStep) installationStep.dataset.flowStage = 'installation_method';
      if (legacyMountStep) {
        legacyMountStep.hidden = false;
        legacyMountStep.dataset.flowStage = 'mount_location';
      }
      if (canonicalAccessories) canonicalAccessories.hidden = true;
      if (windowDetailsStep) windowDetailsStep.dataset.flowStage = 'remaining_details';
      const measurementsStep = section.querySelector('[data-flow-stage="measurements"]');
      const measurementConfirmation = measurementsStep?.querySelector('.ndr-measure-confirmation');
      if (mountDetailFields && measurementConfirmation) measurementsStep.insertBefore(mountDetailFields, measurementConfirmation);
      if (legacyFlow) legacyFlow.hidden = false;
    }
    applyFlowStagePresentation(section, rules);
    applyCustomizerFlowOrder(section, rules);
    renumberVisibleSteps(section);
    if (!rules.steppedBuilder) placeMobilePreview(section);
    setupMobilePreviewSpacing(section);
    setupMobilePreviewZoom(section);
    populateMeasurements(form, rules);
    applyQuerySelection(form, rules, categoryApi, locks);
    applyExactSceneReviewPreset(form, rules, categoryApi, locks, visualAssets);

    const priceNode = section.querySelector('[data-live-price]');
    const livePriceLabel = section.querySelector('[data-live-price-label]');
    const previewPrice = section.querySelector('[data-preview-price]');
    const previewPriceLabel = section.querySelector('[data-preview-price-label]');
    const summaryPrice = section.querySelector('[data-summary-price]');
    const summaryList = section.querySelector('[data-configuration-summary]');
    const statusNode = section.querySelector('[data-configurator-status]');
    const checkoutGate = section.querySelector('[data-configurator-submit]');
    const addAnotherButton = section.querySelector('[data-add-another-window]');
    const reviewOrderButton = section.querySelector('[data-review-order]');
    const errorSummary = section.querySelector('[data-error-summary]');
    const openingMaterialWarning = section.querySelector('[data-opening-material-warning]');
    const orderNote = section.querySelector('[data-order-note]');
    const pricePending = priceIsPending(rules);
    const estimatedPricing = globalThis.NoDrillBluewaterEstimatedPricing;
    const tieredPricing = rules.mode === 'catalog-v2' && rules.id === 'roller-shades'
      ? globalThis.NoDrillTieredPricePreview : null;
    if (pricePending) {
      const priceNote = section.querySelector('[data-live-price-note]');
      const summaryPriceLabel = section.querySelector('[data-summary-price-label]');
      if (priceNote) priceNote.textContent = 'No price yet.';
      if (summaryPriceLabel) summaryPriceLabel.textContent = 'Price status';
    }

    let configurationId = createConfigurationId();
    let order = loadOrder(core);
    const showStandardBracketNotice = setupStandardBracketDialog(section);
    let previousOptions = selectedOptions(form, rules);
    let editingConfigurationId = '';
    let builderSteps = null;

    function renderOrderNote() {
      if (pricePending) {
        orderNote.textContent = 'You cannot save or order yet.';
        return;
      }
      const pending = order.length;
      orderNote.textContent = pending
        ? `${pending} saved ${pending === 1 ? 'window' : 'windows'} in this order. Add this window or check the order by room.`
        : 'Set up this window. Then add another or check your order.';
    }

    function render() {
      syncTapeMeasures(form);
      reconcileInstallationMountState(form, rules, core, previousOptions);
      stabilizeOptionSelections(form, rules, categoryApi, locks, core);
      let selections = selectedOptions(form, rules);
      if (rules.id === 'roller-shades') {
        const reconciled = core.reconcileInstallationMountTransition(previousOptions, selections);
        if (reconciled.options.mount_location !== selections.mount_location) {
          setOptionValue(form, 'mount_location', reconciled.options.mount_location);
          stabilizeOptionSelections(form, rules, categoryApi, locks, core);
          selections = selectedOptions(form, rules);
        }
      }
      syncInstallationMountPresentation(section, rules, selections, core);
      syncInstallationFirstPresentation(section, rules, selections);
      syncControls(form, rules, categoryApi, selections);
      if (rules.measurement?.status !== 'unresolved') {
        limitSizeChoices(form, core.resolveMeasurementRules(readConfiguration(form, rules), rules));
      }
      syncMeasurementGuidance(section, form, selections, rules, core);
      previousOptions = { ...selections };
      let configuration = readConfiguration(form, rules);
      syncCompatibility(form, rules, configuration);
      syncControlSide(section, form, configuration);
      syncRollerAddons(section, form, rules, configuration, core);
      configuration = readConfiguration(form, rules);
      if (tieredPricing) syncAddonPriceLabels(section, configuration, tieredPricing, form.dataset.heightEntered === 'true');

      const measurementPending = rules.measurement?.status === 'unresolved';
      const measurementRules = core.resolveMeasurementRules(configuration, rules);
      const widthResult = measurementPending
        ? { valid: false, value: null }
        : core.validateMeasurement(configuration.width, 'width', measurementRules);
      const heightResult = measurementPending
        ? { valid: false, value: null }
        : core.validateMeasurement(configuration.height, 'height', measurementRules);
      const openingResult = core.validateOpeningMaterial(
        configuration.openingMaterial,
        configuration.openingMaterialCautionAcknowledged,
      );
      const price = core.calculatePrice(configuration, rules);
      const estimatedPrice = estimatedPricing?.priceConfiguration
        ? estimatedPricing.priceConfiguration(configuration)
        : { valid: false, applicable: false, status: 'unavailable', totalCents: null };
      const startingEstimate = !estimatedPrice.valid && estimatedPrice.applicable
        ? bluewaterStartingEstimate(configuration, estimatedPricing)
        : null;
      const tieredEstimate = tieredPricing?.priceConfiguration(configuration);
      const windowEstimate = priceWindowAddons(configuration, tieredEstimate, tieredPricing, core, rules);
      const displayEstimate = tieredEstimate
        ? (tieredEstimate.valid ? tieredEstimate : null)
        : estimatedPrice.valid ? estimatedPrice : startingEstimate;
      const displayingStartingPrice = Boolean(displayEstimate?.starting);
      const validation = core.validateConfiguration(configuration, rules);
      const selectedMount = rules.mounts[configuration.mountSystem];
      const selectedControl = rules.controls[configuration.control];
      const mountFact = section.querySelector('[data-mount-fact]');
      if (mountFact && Array.isArray(rules.customizerFlow)) {
        mountFact.textContent = installationLabel(rules, configuration.options.installation_method, configuration.options.installation_method === 'no-drill'
          ? 'No-Drill · inside mount'
          : configuration.options.installation_method === 'standard-bracket'
            ? 'Standard bracket mount'
            : 'Choose how to mount');
      }
      const measurementFact = section.querySelector('[data-sizing-fact]');
      if (measurementFact) {
        measurementFact.textContent = 'Made to your size';
      }
      const options = optionSummary(configuration, rules, categoryApi);
      const selectedUpgrades = configuration.upgrades.map((id) => rules.upgrades[id]?.label).filter(Boolean);
      const selectedAccessories = configuration.accessories.map((id) => rules.accessories?.[id]?.label).filter(Boolean);

      const estimateApplicable = Boolean(estimatedPrice.applicable);
      const formattedPrice = windowEstimate
        ? windowEstimate.valid ? formatMoney(windowEstimate.totalCents, rules.currency)
          : configuration.control === 'motorized' && !configuration.remoteType ? 'Choose a remote'
            : configuration.remoteType === 'none' && !configuration.compatibleRemoteAcknowledgement ? 'Confirm remote'
              : 'Finish your choices'
        : displayEstimate?.valid
        ? formatMoney(displayEstimate.totalCents || 0, displayEstimate.currency || rules.currency)
        : estimateApplicable
          ? estimatedPrice.status === 'price-pending' ? 'Price pending' : 'Select options'
          : pricePending
            ? 'Price pending'
            : formatMoney(price.totalCents || 0, rules.currency);
      priceNode.textContent = formattedPrice;
      priceNode.toggleAttribute('data-price-state', !formattedPrice.startsWith('$'));
      if (previewPrice) previewPrice.textContent = formattedPrice;
      summaryPrice.textContent = formattedPrice;
      if (livePriceLabel) livePriceLabel.textContent = displayingStartingPrice ? 'Starting at' : 'Your shade setup';
      if (previewPriceLabel) previewPriceLabel.textContent = displayingStartingPrice ? 'Starting at' : 'Price for your choices';
      const priceNote = section.querySelector('[data-live-price-note]');
      const summaryPriceLabel = section.querySelector('[data-summary-price-label]');
      if (tieredEstimate) {
        const totalRow = summaryPrice.closest('.ndr-summary__price');
        if (totalRow) totalRow.dataset.windowTotal = '';
        const label = displayingStartingPrice ? 'Starting at' : 'Total for this window';
        if (livePriceLabel) livePriceLabel.textContent = label;
        if (previewPriceLabel) previewPriceLabel.textContent = label;
        if (summaryPriceLabel) summaryPriceLabel.textContent = label;
        if (priceNote) priceNote.textContent = tieredEstimate.valid
          ? 'Your price for the size and choices you picked.'
          : 'One of your choices has no price yet.';
        section.querySelectorAll('[data-price-tier]').forEach((node) => {
          node.textContent = tieredEstimate.tierLabel ? `${tieredEstimate.tierLabel} collection` : '';
          node.hidden = !tieredEstimate.tierLabel;
        });
      } else if (estimatedPrice.valid) {
        if (priceNote) priceNote.textContent = 'Estimated shade price. Free shipping on 2+ shades.';
        if (summaryPriceLabel) summaryPriceLabel.textContent = 'Estimated price';
      } else if (displayingStartingPrice) {
        if (priceNote) priceNote.textContent = 'Starting price. Choose a control to update the estimate. Free shipping on 2+ shades.';
        if (summaryPriceLabel) summaryPriceLabel.textContent = 'Starting at';
      } else if (estimateApplicable) {
        if (priceNote) priceNote.textContent = estimatedPrice.status === 'price-pending'
          ? 'One of your choices has no price yet.'
          : 'Finish the choices that have a price.';
        if (summaryPriceLabel) summaryPriceLabel.textContent = 'Estimated price';
      }
      // Black Friday sale (NDR-D-145): one line by the builder price while the sale runs.
      const dealLine = section.querySelector('[data-builder-deal]');
      if (dealLine) dealLine.hidden = !saleBandShown();
      const validationCopy = section.querySelector('[data-category-validation]');
      if (validationCopy) {
        validationCopy.textContent = tieredEstimate
          ? 'Your price changes as you pick a size and options.'
          : estimateApplicable
          ? 'Your price changes as you pick a size and options.'
          : rules.validationLabel;
      }
      syncEstimatedOptionPriceLabels(section, configuration, estimatedPricing);
      if (tieredEstimate) {
        const controlEstimate = (control) => tieredPricing.priceConfiguration({
          ...configuration,
          control,
          options: { ...configuration.options, chain_material: '' },
        });
        // Owner rule: the base (cheapest) option shows no price; every other option shows only
        // what it adds over the base. The total lives in the main price only. Nothing reads cheaper.
        const showAdder = (badge, estimate, base) => {
          if (!badge) return;
          if (!estimate.valid || !base?.valid) {
            badge.hidden = false;
            badge.textContent = 'Price pending';
            return;
          }
          const delta = Math.max(0, estimate.totalCents - base.totalCents);
          badge.hidden = delta === 0;
          badge.textContent = delta === 0 ? '' : `+${formatMoney(delta, estimate.currency)}`;
        };
        const controlInputs = [...section.querySelectorAll('[name="control"]')]
          .filter((input) => !input.disabled && ['cordless', 'motorized'].includes(input.value));
        const controlEstimates = controlInputs.map((input) => controlEstimate(input.value));
        const baseControl = controlEstimate('cordless');
        controlInputs.forEach((input, index) => {
          showAdder(input.closest('label')?.querySelector('.ndr-choice__price'), controlEstimates[index], baseControl);
        });
      }
      section.querySelectorAll('[name="chain_material"]').forEach((input) => {
        const badge = input.closest('label')?.querySelector('.ndr-choice__price');
        if (badge) { badge.hidden = true; badge.textContent = ''; }
      });

      const summary = [
        ['Category', rules.displayLabel],
        ...options.map((option) => [option.label, option.value]),
        ['Mount', selectedMount?.label || 'Choose a mount'],
        ...depthSummaryRows(configuration, rules, core),
        ['Production width', measurementPending ? 'No sizes yet' : (widthResult.valid ? core.formatInches(widthResult.value) : 'Check again')],
        ['Height', measurementPending ? 'No sizes yet' : (heightResult.valid ? core.formatInches(configuration.height) : 'Check again')],
        ['Mounting surface', mountingSurfaceLabel(configuration.mountingSurface)],
        ['Mounting point material', form.elements.openingMaterial.selectedOptions[0]?.textContent || 'Choose a material'],
        ['Room', configuration.roomLabel ? `${configuration.room || 'Room'} · ${configuration.roomLabel}` : configuration.room || 'Choose a room'],
        ['Control', selectedControl?.label || 'Choose a control'],
        [rules.id === 'roller-shades' ? 'Accessories' : 'Upgrades', rules.id === 'roller-shades'
          ? (selectedAccessories.length ? selectedAccessories.join(', ') : 'None')
          : (selectedUpgrades.length ? selectedUpgrades.join(', ') : 'None')],
      ];
      const steppedRows = rules.steppedBuilder ? globalThis.NoDrillBuilderSteps?.reviewRows?.(configuration, rules, core) : null;
      if (windowEstimate && !steppedRows) {
        const money = (cents) => cents === null ? 'Select options' : formatMoney(cents, rules.currency);
        const channelLabel = { none: 'No side channels', l_channel: 'L channel', u_channel: 'U channel' }[configuration.sideChannel];
        const remoteLabel = { one_channel: '1-channel remote', six_channel: '6-channel remote', sixteen_channel: '16-channel remote', none: 'No remote with this shade' }[configuration.remoteType];
        summary.push(['Shade price', money(windowEstimate.shadeCents)]);
        if (core.sideChannelsAvailable(configuration, rules)) summary.push(['Side channels', `${channelLabel} · ${money(windowEstimate.sideChannelCents)}`]);
        if (configuration.control === 'motorized') summary.push(['Remotes', remoteLabel
          ? `${remoteLabel}${configuration.remoteType === 'none' ? '' : ` × ${configuration.remoteQuantity}`} · ${money(windowEstimate.remoteCents)}`
          : 'Choose a remote']);
      }
      summaryList.replaceChildren();
      if (steppedRows) {
        steppedRows.forEach((row) => {
          const term = document.createElement('dt');
          term.textContent = row.label;
          const detail = document.createElement('dd');
          const value = document.createElement('span');
          value.textContent = row.value;
          const change = document.createElement('button');
          change.type = 'button';
          change.dataset.builderChange = row.stage;
          change.textContent = 'Change';
          change.setAttribute('aria-label', `Change ${row.label.toLowerCase()}`);
          detail.append(value, change);
          summaryList.append(term, detail);
        });
      } else summary.forEach(([label, value]) => {
        const term = document.createElement('dt');
        term.textContent = label;
        const detail = document.createElement('dd');
        detail.textContent = value;
        summaryList.append(term, detail);
      });

      const cautionRequired = openingResult.requiresCaution;
      openingMaterialWarning.hidden = !cautionRequired;
      if (!cautionRequired) form.elements.openingMaterialCautionAcknowledged.checked = false;

      const swatchGroup = rules.optionGroups.find((group) => group.id === 'fabric_color');
      const swatch = swatchGroup?.options.find((option) => option.id === configuration.options[swatchGroup.id]);
      const previewSwatch = section.querySelector('[data-preview-swatch]');
      const previewSwatchChip = section.querySelector('[data-preview-swatch-chip]');
      const previewSwatchLabel = section.querySelector('[data-preview-swatch-label]');
      syncPreviewSwatchChip(previewSwatchChip, swatch);
      if (previewSwatchLabel) previewSwatchLabel.textContent = swatch?.label || 'Choose a color';
      if (previewSwatch) previewSwatch.dataset.hasSelection = swatch ? 'true' : 'false';
      syncProductVisualization(section, {
        ...configuration.options,
        control_method: configuration.control,
        accessory_visual_key: configuration.accessories.length ? configuration.accessories.join('+') : 'none',
      }, rules, categoryApi, visualAssets);
      syncFinishCoordinationWarning(section, configuration, rules, core);
      updateProgress(section, configuration, validation, rules, core);

      addAnotherButton.disabled = pricePending || !validation.valid || !price.valid;
      reviewOrderButton.disabled = pricePending || !validation.valid || !price.valid;
      statusNode.textContent = displayEstimate?.valid && !displayingStartingPrice
        ? (validation.valid
            ? 'Your price changes as you choose.'
            : 'Fill in all required shade details.')
        : displayingStartingPrice
          ? 'This is the starting price. Choose a control to see your price.'
        : pricePending
          ? (validation.valid
              ? 'Your shade setup is done. No price yet.'
              : 'Fill in all required fields. No price yet.')
          : validation.valid && price.valid
            ? 'Your shade setup is ready to save and check.'
            : 'Fill in each part you need: shade type, mount, size, room and control. Check the boxes you need too.';

      errorSummary.hidden = true;
      errorSummary.replaceChildren();
      renderOrderNote();
      section.dataset.configuration = JSON.stringify({
        ...configuration,
        categoryId: rules.id,
        priceStatus: pricePending ? 'price-pending' : (rules.priceStatus || 'priced'),
        totalCents: pricePending ? null : (price.totalCents || 0),
      });
      builderSteps?.refresh();
    }

    function showErrors(errors) {
      const unique = [...new Set(errors.filter(Boolean))];
      const heading = document.createElement('strong');
      heading.textContent = 'Complete these items:';
      const list = document.createElement('ul');
      unique.forEach((error) => {
        const item = document.createElement('li');
        item.textContent = error;
        list.append(item);
      });
      errorSummary.replaceChildren(heading, list);
      errorSummary.hidden = false;
      errorSummary.focus();
    }

    function currentSavedConfiguration() {
      if (pricePending) return null;
      const configuration = readConfiguration(form, rules);
      const validation = core.validateConfiguration(configuration, rules);
      const price = core.calculatePrice(configuration, rules);
      if (!validation.valid || !price.valid) {
        showErrors([...validation.errors, ...(price.errors || [])]);
        return null;
      }
      const openingMaterialSelect = form.elements.openingMaterial;
      const installationGroup = rules.optionGroups.find((group) => group.id === 'installation_method');
      const mountGroup = rules.optionGroups.find((group) => group.id === 'mount_location');
      const installationMethodLabel = installationGroup?.options.find(
        (option) => option.id === configuration.options.installation_method,
      )?.label || '';
      const mountLocationLabel = mountGroup?.options.find(
        (option) => option.id === configuration.options.mount_location,
      )?.label || '';
      return {
        ...configuration,
        categoryId: rules.id,
        categoryLabel: rules.displayLabel,
        optionSummary: optionSummary(configuration, rules, categoryApi),
        configurationId,
        editUrl: (() => {
          const url = new URL(globalThis.location.href);
          url.searchParams.delete('edit_configuration');
          return `${url.pathname}${url.search}${url.hash}`;
        })(),
        productionWidth: price.productionWidth,
        mountingSurfaceLabel: mountingSurfaceLabel(configuration.mountingSurface),
        openingMaterialLabel: openingMaterialSelect.selectedOptions[0]?.textContent || configuration.openingMaterial,
        installationMethodLabel,
        mountLocationLabel,
        mountSystemLabel: rules.mounts[configuration.mountSystem].label,
        controlLabel: rules.controls[configuration.control].label,
        upgradeLabels: configuration.upgrades.map((id) => rules.upgrades[id].label),
        accessoryLabels: configuration.accessories.map((id) => rules.accessories?.[id]?.label).filter(Boolean),
        priceBandId: price.priceBandId,
        variantId: price.variantId,
        totalCents: price.totalCents,
      };
    }

    function replaceSavedConfiguration(saved) {
      const index = order.findIndex((item) => item.configurationId === editingConfigurationId);
      if (index >= 0) order[index] = saved;
      else order.push(saved);
    }

    function saveCurrentConfiguration() {
      const saved = currentSavedConfiguration();
      if (!saved) return false;
      if (editingConfigurationId) {
        replaceSavedConfiguration(saved);
        editingConfigurationId = '';
      } else {
        order.push(saved);
      }
      saveOrder(order);
      return true;
    }

    function restoreSavedConfiguration(saved) {
      if (!saved || saved.categoryId !== rules.id || pricePending) return false;
      const savedRules = core.resolveMeasurementRules(saved, categoryApi.buildRules(rules.id, saved.options));
      if (!core.validateMeasurement(saved.width, 'width', savedRules).valid
        || !core.validateMeasurement(saved.height, 'height', savedRules).valid) {
        form.elements.measurementConfirmed.checked = false;
        showErrors(['Please pick your sizes again and confirm them.']);
        return false;
      }
      editingConfigurationId = saved.configurationId;
      configurationId = saved.configurationId;
      Object.entries(saved.options || {}).forEach(([groupId, value]) => setOptionValue(form, groupId, value));
      syncOptionDependencies(form, rules, categoryApi, locks);
      const width = Number(saved.width);
      const height = Number(saved.height);
      resetSizeChoices(form, rules);
      form.elements.widthWhole.value = String(Math.floor(width));
      form.elements.widthFraction.value = String(width - Math.floor(width));
      form.elements.heightWhole.value = String(Math.floor(height));
      form.elements.heightFraction.value = String(height - Math.floor(height));
      form.elements.room.value = saved.room || '';
      form.elements.roomLabel.value = saved.roomLabel || '';
      form.querySelectorAll('[name="mountingSurface"]').forEach((input) => {
        input.checked = input.value === saved.mountingSurface;
      });
      form.elements.openingMaterial.value = saved.openingMaterial || '';
      form.elements.openingMaterialCautionAcknowledged.checked = Boolean(saved.openingMaterialCautionAcknowledged);
      form.elements.noDrillDepthConfirmed.checked = Boolean(saved.noDrillDepthConfirmed);
      form.elements.measurementConfirmed.checked = Boolean(saved.measurementConfirmed);
      form.elements.compatibilityConfirmed.checked = Boolean(saved.compatibilityConfirmed);
      form.querySelectorAll('[name="control"]').forEach((input) => { input.checked = input.value === saved.control; });
      form.querySelectorAll('[name="controlSide"]').forEach((input) => {
        input.checked = input.value === (saved.controlSide === 'left' ? 'left' : 'right');
      });
      const accessory = saved.accessories?.[0] || 'none';
      form.querySelectorAll('[name="accessory_choice"]').forEach((input) => { input.checked = input.value === accessory; });
      previousOptions = { ...(saved.options || {}) };
      render();
      focusStage(section, 'installation_mount');
      return true;
    }

    function resetWindowSpecificFields() {
      const defaultWidth = String(previewDefaultSize(rules.measurement, 'width'));
      form.elements.widthWhole.value = defaultWidth;
      form.elements.widthFraction.value = '0';
      form.elements.heightWhole.value = String(previewDefaultSize(rules.measurement, 'height'));
      form.elements.heightFraction.value = '0';
      form.elements.room.value = '';
      form.elements.roomLabel.value = '';
      form.querySelectorAll('[name="mountingSurface"]').forEach((input) => { input.checked = false; });
      form.elements.openingMaterial.value = '';
      form.elements.openingMaterialCautionAcknowledged.checked = false;
      form.elements.noDrillDepthConfirmed.checked = false;
      form.elements.measurementConfirmed.checked = false;
      form.elements.compatibilityConfirmed.checked = false;
      form.querySelectorAll('[name="controlSide"]').forEach((input) => { input.checked = input.value === 'right'; });
      configurationId = createConfigurationId();
      editingConfigurationId = '';
      render();
      section.querySelector('[data-flow-stage="measurements"]')?.scrollIntoView({ behavior: preferredScrollBehavior(), block: 'start' });
      form.elements.widthWhole.focus();
    }

    function restoreCartEditConfiguration(id) {
      let saved;
      try {
        const records = JSON.parse(globalThis.localStorage.getItem('ndr-cart-edit-v1') || '{}');
        const isRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);
        if (!id || !isRecord(records) || !Object.prototype.hasOwnProperty.call(records, id)) return false;
        const record = records[id];
        if (!isRecord(record) || record.v !== 1 || !Number.isFinite(record.savedAt)
          || record.savedAt <= 0 || typeof record.editUrl !== 'string' || !record.editUrl) return false;
        saved = record.configuration;
        if (!isRecord(saved) || saved.categoryId !== rules.id || !isRecord(saved.options)
          || !Object.values(saved.options).every((value) => typeof value === 'string')) return false;
        const strings = ['room', 'roomLabel', 'control', 'controlSide', 'mountSystem', 'mountingSurface',
          'openingMaterial', 'sideChannel', 'remoteType', 'accessoryChoice'];
        const confirmations = ['openingMaterialCautionAcknowledged', 'noDrillDepthConfirmed',
          'measurementConfirmed', 'compatibilityConfirmed', 'compatibleRemoteAcknowledgement'];
        if (strings.some((key) => saved[key] !== undefined && typeof saved[key] !== 'string')
          || confirmations.some((key) => saved[key] !== undefined && typeof saved[key] !== 'boolean')
          || ['upgrades', 'accessories'].some((key) => saved[key] !== undefined
            && (!Array.isArray(saved[key]) || !saved[key].every((value) => typeof value === 'string')))) return false;
        const validQuantity = (value) => Number.isInteger(value) && value >= 0;
        if (saved.remoteQuantity !== undefined && !validQuantity(saved.remoteQuantity)) return false;
        for (const key of ['orderAccessoryQuantities', 'accessoryQuantities']) {
          if (saved[key] === undefined) continue;
          if (!isRecord(saved[key]) || !Object.entries(saved[key]).every(([name, value]) => (
            name === 'compatible_remote_acknowledged' ? typeof value === 'boolean' : validQuantity(value)
          ))) return false;
        }
        const editRules = core.resolveMeasurementRules(saved, categoryApi.buildRules(rules.id, saved.options));
        if (!core.validateMeasurement(saved.width, 'width', editRules).valid
          || !core.validateMeasurement(saved.height, 'height', editRules).valid) return false;
      } catch (error) {
        return false;
      }

      rules.optionGroups.forEach((group) => setOptionValue(form, group.id, saved.options[group.id] || ''));
      syncOptionDependencies(form, rules, categoryApi, locks);
      const width = Number(saved.width);
      const height = Number(saved.height);
      resetSizeChoices(form, rules);
      form.elements.widthWhole.value = String(Math.floor(width));
      form.elements.widthFraction.value = String(width - Math.floor(width));
      form.elements.heightWhole.value = String(Math.floor(height));
      form.elements.heightFraction.value = String(height - Math.floor(height));
      form.dataset.heightEntered = 'true';
      form.elements.room.value = saved.room || '';
      // An automatic name ("Kitchen, Window 1") stays automatic, so it follows the room if that changes.
      form.elements.roomLabel.value = rules.steppedBuilder && saved.autoWindowName === true ? '' : saved.roomLabel || '';
      if (form.elements.mountSystem) form.elements.mountSystem.value = saved.mountSystem || '';
      form.elements.openingMaterial.value = saved.openingMaterial || '';
      ['openingMaterialCautionAcknowledged', 'noDrillDepthConfirmed', 'measurementConfirmed',
        'compatibilityConfirmed'].forEach((name) => {
        if (form.elements[name]) form.elements[name].checked = Boolean(saved[name]);
      });
      const choices = {
        mountingSurface: saved.mountingSurface, control: saved.control,
        controlSide: saved.controlSide === 'left' ? 'left' : 'right',
        sideChannel: saved.sideChannel || 'none', remoteType: saved.remoteType || '',
        accessory_choice: saved.accessoryChoice || saved.accessories?.[0] || 'none',
      };
      Object.entries(choices).forEach(([name, value]) => {
        form.querySelectorAll(`[name="${name}"]`).forEach((input) => { input.checked = input.value === value; });
      });
      form.querySelectorAll('[name="upgrades"]').forEach((input) => {
        input.checked = (saved.upgrades || []).includes(input.value);
      });
      if (form.elements.remoteQuantity) form.elements.remoteQuantity.value = String(saved.remoteQuantity ?? 1);
      if (form.elements.compatibleRemoteAcknowledged) {
        form.elements.compatibleRemoteAcknowledged.checked = Boolean(saved.compatibleRemoteAcknowledgement
          ?? saved.orderAccessoryQuantities?.compatible_remote_acknowledged);
      }
      Object.entries({ one_channel_remote: 'oneChannelRemoteQuantity', six_channel_remote: 'sixChannelRemoteQuantity',
        sixteen_channel_remote: 'sixteenChannelRemoteQuantity' }).forEach(([key, name]) => {
        if (form.elements[name]) form.elements[name].value = String(saved.orderAccessoryQuantities?.[key] ?? 0);
      });
      previousOptions = { ...saved.options };
      // Restoring an Outside shade is not a customer change from the fresh Inside preview.
      form.dataset.measurementMountLocation = saved.options.mount_location || 'inside';
      render();
      return true;
    }

    const eventRoot = rules.steppedBuilder ? section : form;
    // Stepped builder wall question (Albert, Oct 19): copy the tapped material into the order field before
    // render(), on input as well as change. The old dropdown copied only on change, after the input redraw
    // had already put the first answer back, so a second pick never stuck.
    const syncWallMaterial = (event) => {
      if (event.target?.name !== 'mountMaterial' || !event.target.checked) return;
      if (form.elements.openingMaterial.value === event.target.value) return;
      form.elements.openingMaterial.value = event.target.value;
      form.elements.openingMaterialCautionAcknowledged.checked = false;
    };
    eventRoot.addEventListener('input', (event) => {
      if (event.target?.name === 'option_hardware_finish') return;
      syncWallMaterial(event);
      if (['heightWhole', 'heightFraction'].includes(event.target?.name)) form.dataset.heightEntered = 'true';
      render();
    });
    eventRoot.addEventListener('change', (event) => {
      syncWallMaterial(event);
      if (['heightWhole', 'heightFraction'].includes(event.target?.name)) form.dataset.heightEntered = 'true';
      if (event.target?.name === 'option_hardware_finish' && event.target.checked) {
        const selections = selectedOptions(form, rules);
        if (selections.bottom_rail === 'regular'
          && ['no-drill-open-roll', 'standard-open-roll'].includes(selections.headrail_style)) {
          setOptionValue(form, 'bottom_rail_finish', event.target.value);
        }
      }
      render();
      if (event.target?.name === 'option_installation_method'
        && event.target.checked
        && event.target.value === 'standard-bracket') {
        showStandardBracketNotice();
      }
    });
    addAnotherButton.addEventListener('click', () => {
      if (saveCurrentConfiguration()) resetWindowSpecificFields();
    });
    reviewOrderButton.addEventListener('click', () => {
      if (pricePending) return;
      const configuration = readConfiguration(form, rules);
      const hasStartedWindow = Boolean(configuration.room || configuration.roomLabel || configuration.measurementConfirmed);
      if ((hasStartedWindow || order.length === 0) && !saveCurrentConfiguration()) return;
      globalThis.location.href = globalThis.location.pathname.endsWith('.html') ? 'cart.html' : `${globalThis.Shopify?.routes?.root || '/'}cart`;
    });
    // Some categories rename the headrail step to hardware_finish at load (flowStageAliases), so find whichever is on the page.
    section.querySelector('[data-edit-headrail]')?.addEventListener('click', () => focusStage(
      section,
      section.querySelector('[data-flow-stage="hardware_finish"]') ? 'hardware_finish' : 'headrail_style',
    ));
    section.querySelector('[data-edit-bottom-rail]')?.addEventListener('click', () => focusStage(section, 'bottom_rail'));

    // The cart integration publishes availability only; it must never answer the remote choice.
    section.addEventListener('ndr:cart-remote-status', (event) => {
      section.dataset.cartHasRemote = event.detail?.hasRemote === true ? 'true' : 'false';
      render();
    });

    const edit_configuration = new URLSearchParams(globalThis.location.search).get('edit_configuration');
    if (edit_configuration) {
      const saved = order.find((item) => item.configurationId === edit_configuration);
      if (saved) restoreSavedConfiguration(saved);
    }

    async function addToShopifyCart(configuration, price) {
      if (!rules.checkoutEnabled) return;
      if (pricePending || !core.validateConfiguration(configuration, rules).valid || !price.valid) return;
      const properties = core.buildLineItemProperties({
        ...configuration,
        categoryLabel: rules.displayLabel,
        optionSummary: optionSummary(configuration, rules, categoryApi),
        configurationId,
        mountSystemLabel: rules.mounts[configuration.mountSystem].label,
        controlLabel: rules.controls[configuration.control].label,
        upgradeLabels: configuration.upgrades.map((id) => rules.upgrades[id].label),
        accessoryLabels: configuration.accessories.map((id) => rules.accessories?.[id]?.label).filter(Boolean),
        priceBandId: price.priceBandId,
        productionWidth: price.productionWidth,
        mountingSurfaceLabel: mountingSurfaceLabel(configuration.mountingSurface),
        openingMaterialLabel: form.elements.openingMaterial.selectedOptions[0]?.textContent || configuration.openingMaterial,
      });
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: price.variantId, quantity: 1, properties }] }),
      });
    }

    checkoutGate.addEventListener('click', () => {
      const configuration = readConfiguration(form, rules);
      const price = core.calculatePrice(configuration, rules);
      addToShopifyCart(configuration, price);
    });

    render();
    const editLine = new URLSearchParams(globalThis.location.search).get('edit_line');
    if (editLine !== null) {
      if (restoreCartEditConfiguration(editLine)) {
        section.dataset.editLine = editLine;
        statusNode.textContent = 'You are changing a shade in your cart. Save to update it.';
        focusStage(section, 'review');
      } else {
        section.dataset.editLineMissing = 'true';
        statusNode.textContent = 'We could not open that shade. Set it up again, or remove it in your cart.';
      }
    }
    if (rules.steppedBuilder) builderSteps = globalThis.NoDrillBuilderSteps?.setup(section, rules, core);
  }

  function boot() {
    document.querySelectorAll('[data-no-drill-configurator]').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
